import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../auth/useAuth';
import { bulkInsertIssues, insertIssue, fetchQcWorkingData, uploadQcWorkingData, clearQcWorkingData } from '../services/dataService';
import { SANDBOX_MODE, SANDBOX_ROWS, SANDBOX_RETAILER, SANDBOX_UPLOAD_PREFIX, isSandboxRetailer } from '../config/sandbox';
import { cn } from '../utils/cn';
import { validateSpotLink, SPOT_LINK_FORMAT_HINT } from '../utils/spotLink';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  CheckCircle2,
  Circle,
  AlertCircle,
  MessageSquare,
  Layout,
  ClipboardCheck,
  Filter,
  Eye,
  MoreVertical,
  Upload,
  Download,
  Trash2,
  Check,
  X,
  Plus,
  Save,
  RotateCcw
} from 'lucide-react';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

// A spot is "reviewed" under the canonical rule used by the page-complete
// gate, shopper stats, and the sidebar/audit progress indicators. Kept as a
// pure function so all three sites can share it — three inlined copies had
// already drifted apart, causing the shopper "5/6" counter and the sidebar
// ✓ to disagree with the "Push Page" enable state.
export function isSpotReviewed(spot, qcState) {
  const spotState = qcState[`spot-${spot.spotId}`];
  if (spotState?.status === 'pass' || spotState?.status === 'fail') return true;
  if (spotState?.issueType) return true;
  if (spot.products?.length) {
    return spot.products.some((product) => {
      const productState = qcState[`product-${product.upc}-spot-${spot.spotId}`];
      return productState?.issueType || productState?.status;
    });
  }
  return false;
}

// Rolls per-spot review state into a page-level status so the sidebar can
// visually surface "which pages did the QC skip?" at a glance while scrolling.
// status: 'empty' (no spots touched) | 'partial' (some, not all) | 'complete' (all).
export function getPageReviewStatus(page, qcState) {
  const spots = Object.values(page?.spots ?? {});
  const total = spots.length;
  const reviewed = spots.filter((s) => isSpotReviewed(s, qcState)).length;
  const ratio = total > 0 ? reviewed / total : 0;
  const status = total === 0 || reviewed === 0
    ? 'empty'
    : reviewed === total
      ? 'complete'
      : 'partial';
  return { reviewed, total, ratio, status };
}

const App = () => {
  const { user } = BYPASS_AUTH ? {} : useAuth();
  
  // --- STATE ---
  const [data, setData] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState('All');
  const [selectedDate, setSelectedDate] = useState('All');
  const [activeView, setActiveView] = useState('dashboard');
  const [currentShopperKey, setCurrentShopperKey] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedSpotImage, setSelectedSpotImage] = useState(null);
  // Spot image zoom/pan
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // Page image zoom/pan
  const [pageImageZoom, setPageImageZoom] = useState(1);
  const [pageImagePan, setPageImagePan] = useState({ x: 0, y: 0 });
  const [isPageDragging, setIsPageDragging] = useState(false);
  const [pageDragStart, setPageDragStart] = useState({ x: 0, y: 0 });
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedPagesForExport, setSelectedPagesForExport] = useState([]);
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Database persistence state
  const [hasDbData, setHasDbData] = useState(null); // null = loading, true/false = checked
  const [isLoadingDb, setIsLoadingDb] = useState(true);
  const [isClearingData, setIsClearingData] = useState(false);
  
  // New Product Form state
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({});
  const [newProducts, setNewProducts] = useState([]); // Separate state for created products
  const [isSavingNewProduct, setIsSavingNewProduct] = useState(false);
  const [newProductError, setNewProductError] = useState(null);
  // Field-level error for Spot Link, kept apart from newProductError so a
  // format complaint sits under the field it belongs to rather than in the
  // form-wide banner.
  const [spotLinkError, setSpotLinkError] = useState(null);

  // --- DRAW STATE (Missing Spot rectangle marking) ---
  const [drawMode, setDrawMode] = useState(false);
  const [isRectDrawing, setIsRectDrawing] = useState(false);
  const [liveRect, setLiveRect] = useState(null); // { x, y, w, h } in canvas pixels
  const [rectDrawStart, setRectDrawStart] = useState(null); // { x, y } in canvas pixels
  const [drawError, setDrawError] = useState(null);
  const [hoveredHandle, setHoveredHandle] = useState(null); // { type: 'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w', rectKey }
  const [dragHandle, setDragHandle] = useState(null); // { type, startRect, startMouse }
  const [spacebarHeld, setSpacebarHeld] = useState(false); // For pan modifier key
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  // Storage backend. Sandbox state lives in sessionStorage so closing the tab
  // or doing a hard refresh wipes it (matching the "ephemeral sandbox" model);
  // the seeded shoppers are re-injected on next mount from SANDBOX_ROWS. Real
  // QC work continues to use localStorage so progress survives across sessions.
  const qcStorage = SANDBOX_MODE ? sessionStorage : localStorage;
  const QC_STATE_KEY = SANDBOX_MODE ? 'qcTool:sandbox' : 'qcTool';
  // Recovery slot holding the most recent per-shopper QC reset, so it can be undone.
  const QC_RESET_BACKUP_KEY = `${QC_STATE_KEY}:lastReset`;
  const QC_DATA_KEY = 'qcData:sandbox';

  // QC Data: { [key]: { status: 'pass'|'fail', observation: string, manualCount: number, exported: boolean } }
  const [qcState, setQcState] = useState(() => {
    const saved = qcStorage.getItem(QC_STATE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // The most recent per-shopper reset, read from storage so an "Undo reset"
  // affordance can restore it. Kept in sync via the effect below.
  const [resetBackup, setResetBackup] = useState(() => {
    try {
      const raw = qcStorage.getItem(QC_RESET_BACKUP_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  // Save progress to the chosen storage
  useEffect(() => {
    qcStorage.setItem(QC_STATE_KEY, JSON.stringify(qcState));
  }, [qcState]);

  // Keep the "Undo reset" affordance in sync with the stashed backup.
  // resetQCProgress writes the backup to storage and updates qcState, so
  // re-reading here whenever qcState changes surfaces (or clears) the backup
  // without coupling this effect to that handler.
  useEffect(() => {
    try {
      const raw = qcStorage.getItem(QC_RESET_BACKUP_KEY);
      setResetBackup(raw ? JSON.parse(raw) : null);
    } catch (e) {
      setResetBackup(null);
    }
  }, [qcState]);

  // In sandbox mode, persist `data` to sessionStorage too so navigating between
  // QC Tool and Issues Resolution within the same tab keeps the uploaded
  // shopper visible. Hard reload / closing the tab clears it and SANDBOX_ROWS
  // get re-injected on the next mount.
  useEffect(() => {
    if (!SANDBOX_MODE) return;
    if (!data || data.length === 0) return;
    sessionStorage.setItem(QC_DATA_KEY, JSON.stringify(data));
  }, [data]);

  // Build the exact set of qcState keys owned by a list of shoppers, mirroring
  // how the app writes them (page-/count- embed the shopper id; spot-/product-
  // are keyed by the shopper's own spot ids). Used to scope clears/replaces to
  // specific shoppers -- never a global wipe.
  const collectOwnedKeys = (shopperList) => {
    const keys = new Set();
    (shopperList || []).forEach(shopper => {
      Object.entries(shopper.pages).forEach(([pageNum, page]) => {
        keys.add(`page-${shopper.id}-${pageNum}`);
        keys.add(`count-${shopper.id}-${pageNum}`);
        Object.values(page.spots).forEach(spot => {
          keys.add(`spot-${spot.spotId}`);
          (spot.products || []).forEach(product => {
            keys.add(`product-${product.upc}-spot-${spot.spotId}`);
          });
        });
      });
    });
    return keys;
  };

  // Immutable helper: return a copy of `state` with `keysToRemove` dropped.
  const pruneQcState = (state, keysToRemove) => {
    const next = {};
    Object.entries(state).forEach(([k, v]) => {
      if (!keysToRemove.has(k)) next[k] = v;
    });
    return next;
  };

  // Load current working data from the DB into the UI. Shared by the mount
  // effect and the post-upload refetch so the view always reflects the server
  // rows (not just local state). Surfaces load failures instead of swallowing
  // them (e.g. a 42501 permission-denied or replica error stays visible).
  const loadWorkingData = async (options = {}) => {
    const { data: response, error } = await fetchQcWorkingData(options);
    if (error) {
      console.error('Failed to load QC working data:', error);
      setLoadError('Could not load QC working data from the server. The view may be out of date - please retry. If this keeps happening, report it (possible read-permission or replica issue).');
      setHasDbData(false);
      return { ok: false, error };
    }
    if (response?.hasData) {
      // Transform DB rows back to frontend format (snake_case -> camelCase)
      const transformedRows = response.rows.map(row => ({
        retailer: row.retailer || '',
        startDate: row.start_date || '',
        endDate: row.end_date || '',
        pageNumber: row.page_number || 0,
        shopperId: row.shopper_id || '',
        shopperImage: row.shopper_image || '',
        spotId: row.spot_id || '',
        spotImage: row.spot_image || '',
        upc: row.upc || '',
        brand: row.brand || '',
        category: row.category || '',
        description: row.description || '',
        priceReg: row.price_reg || '',
        qtyReg: row.qty_reg || '',
        priceSpe: row.price_spe || '',
        qtySpe: row.qty_spe || '',
      }));
      setData(transformedRows);
      setHasDbData(true);
      console.log('[QCTool] Loaded', transformedRows.length, 'rows from database');
    } else {
      setData([]);
      setHasDbData(false);
    }
    return { ok: true };
  };

  // Check database for existing working data on mount
  useEffect(() => {
    (async () => {
      setIsLoadingDb(true);

      // Sandbox: prefer in-tab session state (so QC↔IR navigation keeps the
      // uploaded shopper alive). DB fetch is intercepted in sandbox anyway.
      if (SANDBOX_MODE) {
        const savedData = sessionStorage.getItem(QC_DATA_KEY);
        if (savedData) {
          try {
            const restored = JSON.parse(savedData);
            if (Array.isArray(restored) && restored.length > 0) {
              setData(restored);
              setHasDbData(true);
              setIsLoadingDb(false);
              console.log('[QCTool] SANDBOX_MODE — restored', restored.length, 'rows from session');
              return;
            }
          } catch (e) {
            console.warn('[QCTool] failed to parse sandbox session data', e);
          }
        }
        setData(SANDBOX_ROWS);
        setHasDbData(true);
        setIsLoadingDb(false);
        console.log('[QCTool] SANDBOX_MODE — injected', SANDBOX_ROWS.length, 'seeded rows');
        return;
      }

      // Delegates to loadWorkingData so mount and the post-upload refetch share
      // one code path, and load failures surface instead of being swallowed.
      await loadWorkingData();
      setIsLoadingDb(false);
    })();
  }, []);

  // Reset draw state when navigating to a different page or shopper
  useEffect(() => {
    setDrawMode(false);
    setIsRectDrawing(false);
    setLiveRect(null);
    setDrawError(null);
    setRectDrawStart(null);
    setHoveredHandle(null);
    setDragHandle(null);
    setPageImageZoom(1);
    setPageImagePan({ x: 0, y: 0 });
  }, [currentPageIndex, currentShopperKey]);

  // Track spacebar for pan modifier
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore spacebar if user is typing in an input/textarea
      const activeElement = document.activeElement;
      const isTyping = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
      
      if (e.code === 'Space' && !e.repeat && !isTyping) {
        setSpacebarHeld(true);
        e.preventDefault(); // Prevent page scroll
      }
    };
    const handleKeyUp = (e) => {
      // Ignore spacebar if user is typing in an input/textarea
      const activeElement = document.activeElement;
      const isTyping = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
      
      if (e.code === 'Space' && !isTyping) {
        setSpacebarHeld(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Sync canvas dimensions and repaint saved rect + live drawing rect + handles
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || canvas.offsetWidth === 0 || canvas.offsetHeight === 0) return;

    // Calculate actual rendered image bounds within container (accounting for object-contain)
    const containerWidth = canvas.offsetWidth;
    const containerHeight = canvas.offsetHeight;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerWidth / containerHeight;
    
    let renderWidth, renderHeight, offsetX, offsetY;
    if (imgAspect > containerAspect) {
      // Image is wider - fits to width
      renderWidth = containerWidth;
      renderHeight = containerWidth / imgAspect;
      offsetX = 0;
      offsetY = (containerHeight - renderHeight) / 2;
    } else {
      // Image is taller - fits to height
      renderHeight = containerHeight;
      renderWidth = containerHeight * imgAspect;
      offsetX = (containerWidth - renderWidth) / 2;
      offsetY = 0;
    }

    // Store these for coordinate mapping
    canvas.dataset.imgRenderWidth = renderWidth;
    canvas.dataset.imgRenderHeight = renderHeight;
    canvas.dataset.imgOffsetX = offsetX;
    canvas.dataset.imgOffsetY = offsetY;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Saved rect — persists after draw mode deactivates
    const pageKey = canvas.dataset.pageKey;
    const savedMeta = pageKey ? qcState[pageKey]?.rect : null;
    if (savedMeta?.rect) {
      const { x, y, w, h } = savedMeta.rect;
      const imgRenderWidth = parseFloat(canvas.dataset.imgRenderWidth) || canvas.width;
      const imgRenderHeight = parseFloat(canvas.dataset.imgRenderHeight) || canvas.height;
      const imgOffsetX = parseFloat(canvas.dataset.imgOffsetX) || 0;
      const imgOffsetY = parseFloat(canvas.dataset.imgOffsetY) || 0;
      
      ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
      ctx.strokeStyle = 'rgb(239, 68, 68)';
      ctx.lineWidth = 2;
      const rectX = imgOffsetX + (x * imgRenderWidth);
      const rectY = imgOffsetY + (y * imgRenderHeight);
      const rectW = w * imgRenderWidth;
      const rectH = h * imgRenderHeight;
      ctx.fillRect(rectX, rectY, rectW, rectH);
      ctx.strokeRect(rectX, rectY, rectW, rectH);

      // Draw adjustment handles (8 total: corners + edges)
      if (!isRectDrawing) {
        const handleSize = 8;
        const imgRenderWidth = parseFloat(canvas.dataset.imgRenderWidth) || canvas.width;
        const imgRenderHeight = parseFloat(canvas.dataset.imgRenderHeight) || canvas.height;
        const imgOffsetX = parseFloat(canvas.dataset.imgOffsetX) || 0;
        const imgOffsetY = parseFloat(canvas.dataset.imgOffsetY) || 0;
        
        const rx = imgOffsetX + (x * imgRenderWidth);
        const ry = imgOffsetY + (y * imgRenderHeight);
        const rw = w * imgRenderWidth;
        const rh = h * imgRenderHeight;
        
        const handles = [
          { type: 'nw', x: rx, y: ry },
          { type: 'n', x: rx + rw/2, y: ry },
          { type: 'ne', x: rx + rw, y: ry },
          { type: 'e', x: rx + rw, y: ry + rh/2 },
          { type: 'se', x: rx + rw, y: ry + rh },
          { type: 's', x: rx + rw/2, y: ry + rh },
          { type: 'sw', x: rx, y: ry + rh },
          { type: 'w', x: rx, y: ry + rh/2 },
        ];

        handles.forEach(handle => {
          const isHovered = hoveredHandle?.type === handle.type;
          ctx.fillStyle = isHovered ? 'rgb(239, 68, 68)' : 'white';
          ctx.strokeStyle = 'rgb(239, 68, 68)';
          ctx.lineWidth = 2;
          ctx.fillRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
          ctx.strokeRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
        });
      }
    }

    // Live rect while dragging new rectangle
    if (liveRect && isRectDrawing && !dragHandle) {
      const imgOffsetX = parseFloat(canvas.dataset.imgOffsetX) || 0;
      const imgOffsetY = parseFloat(canvas.dataset.imgOffsetY) || 0;
      
      // liveRect coords are image-relative (from getCanvasCoords), so add offsets for canvas rendering
      const rx = imgOffsetX + Math.min(liveRect.x, liveRect.x + liveRect.w);
      const ry = imgOffsetY + Math.min(liveRect.y, liveRect.y + liveRect.h);
      const rw = Math.abs(liveRect.w);
      const rh = Math.abs(liveRect.h);
      ctx.fillStyle = 'rgba(239, 68, 68, 0.15)';
      ctx.strokeStyle = 'rgb(239, 68, 68)';
      ctx.lineWidth = 2;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeRect(rx, ry, rw, rh);
    }
  }, [liveRect, isRectDrawing, qcState, currentPageIndex, currentShopperKey, selectedSpotImage, hoveredHandle, dragHandle]);

  // --- CSV PARSING ---
  // Parse a QC CSV file into the row shape the rest of the page uses. Pure —
  // no state writes, no API calls. Throws on empty / unreadable files.
  const parseQcCsvFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const rows = text.split(/\r?\n/).filter(row => row.trim() !== "");
        if (rows.length === 0) return reject(new Error('CSV file is empty'));

        const headerRow = rows[0];
        const tabCount = (headerRow.match(/\t/g) || []).length;
        const commaCount = (headerRow.match(/,/g) || []).length;
        const delimiter = tabCount > commaCount ? '\t' : ',';
        const headers = rows[0].split(delimiter).map(h => h.trim());
        const normalizeHeader = (header) => header.toLowerCase().replace(/[^a-z0-9]/g, '');

        const jsonData = rows.slice(1).map(row => {
          const values = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            if (char === '"') inQuotes = !inQuotes;
            else if (char === delimiter && !inQuotes) {
              values.push(current.trim().replace(/^"|"$/g, ''));
              current = '';
            } else current += char;
          }
          values.push(current.trim().replace(/^"|"$/g, ''));

          const normalizedData = {};
          headers.forEach((header, i) => { normalizedData[normalizeHeader(header)] = values[i] || ''; });
          const getValue = (keys) => {
            for (const key of keys) {
              const normalizedKey = normalizeHeader(key);
              if (normalizedKey in normalizedData && normalizedData[normalizedKey]) return normalizedData[normalizedKey];
            }
            return '';
          };

          return {
            retailer: getValue(['Retailer', 'Customername', 'Customer Name', 'Customer']),
            startDate: getValue(['Start Date', 'Startdate', 'Start Date Time', 'Start']),
            endDate: getValue(['End Date', 'Enddate', 'End Date Time', 'End']),
            pageNumber: parseInt(getValue(['Page Numer', 'Page Number', 'Pagenumber', 'Page'])) || 0,
            shopperId: getValue(['Shopper ID', 'ShopperId', 'Id']),
            shopperImage: getValue(['Shopper Image (Raw)', 'Shopper Image Raw', 'Rawmediaresource', 'Rawmedia']),
            spotId: getValue(['Spot ID', 'SpotId', 'Id (Sw1Dbtables Sw1Spot)']),
            spotImage: getValue(['Spot Image (Raw)', 'Spot Image Raw', 'Rawmediaresource (Sw1Dbtables Sw1Spot)', 'Rawmedia (Sw1Dbtables Sw1Spot)']),
            upc: getValue(['UPC', 'Upc']),
            brand: getValue(['Brand']),
            category: getValue(['Category']),
            description: getValue(['Description', 'Descr', 'Desc']),
            priceReg: getValue(['Price Reg', 'Regularprice', 'Regular Price']),
            qtyReg: getValue(['Prod Qty Reg', 'Prodqtyreg', 'Product Qty Reg']),
            priceSpe: getValue(['Price Spe', 'Specialprice', 'Special Price']),
            qtySpe: getValue(['Prod Qty Spe', 'Prodqtyspec', 'Product Qty Spe'])
          };
        }).filter(item => item.retailer || item.shopperId || item.spotId);

        console.log('💾 QC Tool CSV Debug Info:');
        console.log('  Delimiter detected:', delimiter === '\t' ? 'TAB' : 'COMMA');
        console.log('  Headers:', headers);
        console.log('  Total rows parsed:', jsonData.length);
        if (jsonData.length > 0) console.log('  First row sample:', jsonData[0]);

        resolve(jsonData);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file, 'UTF-8');
  });

  // Shared pre-flight: size, type, concurrency. Returns true if upload may proceed.
  const validateUpload = (file, { allowWhenLoaded }) => {
    if (!file) return false;
    if (hasDbData && !allowWhenLoaded) {
      setLoadError('Data already exists. Clear the current data before uploading new data.');
      return false;
    }
    if (isUploading) {
      setLoadError('Please wait for the current upload to complete');
      return false;
    }
    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setLoadError('File too large. Maximum size is 20MB');
      return false;
    }
    if (!file.name.match(/\.(csv|txt)$/i)) {
      setLoadError('Invalid file type. Please upload a CSV or TXT file');
      return false;
    }
    return true;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting the same corrected file
    // allowWhenLoaded: replacing an already-loaded shopper is now supported via
    // an explicit confirm below, instead of silently rejecting the upload.
    if (!validateUpload(file, { allowWhenLoaded: true })) return;

    // Explicit replace flow: when a shopper is already loaded, confirm before
    // we wipe + replace it (uploadQcWorkingData clears the working table).
    const replacedShoppers = hasDbData ? shoppers : [];
    if (replacedShoppers.length > 0) {
      const names = replacedShoppers
        .map(sh => `${sh.retailer} (${sh.startDate} - ${sh.endDate})`)
        .join(', ');
      if (!confirm(`Replace the currently loaded QC data with this file? This clears the working data and the saved reviews for: ${names}.`)) {
        return;
      }
    }

    setIsUploading(true);
    setLoadError(null);
    try {
      const jsonData = await parseQcCsvFile(file);

      // Keys of the shoppers being replaced, so their stale marks can be dropped
      // (scoped -- never a global wipe) once the replace succeeds.
      const replacedKeys = collectOwnedKeys(replacedShoppers);

      const { error: uploadError } = await uploadQcWorkingData(jsonData);
      if (uploadError) {
        setLoadError('Failed to save data to database. Please try again.');
        return;
      }

      // Drop stale pass/fail/exported marks for the replaced shoppers so the
      // corrected upload does not inherit them. Immutable rebuild.
      if (replacedKeys.size > 0) {
        setQcState(prev => pruneQcState(prev, replacedKeys));
      }

      // Optimistic local view of exactly what we uploaded, then reconcile with
      // the server so we are not relying solely on local state.
      setData(jsonData);
      setHasDbData(true);
      // Read-after-write: reconcile from the PRIMARY so replica lag can't flash
      // the old shopper back over the fresh upload.
      await loadWorkingData({ consistent: true });
      console.log('[QCTool] Saved', jsonData.length, 'rows to database');
    } catch (error) {
      console.error('CSV parsing error:', error);
      setLoadError(`Failed to parse CSV: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Sandbox-only: append an uploaded shopper to the current dataset with the
  // retailer name prefixed [SANDBOX] so downstream surfaces can flag it. No DB
  // write — the global SANDBOX_MODE interception in dataService handles that
  // when the user later pushes issues to the ledger.
  const handleSandboxAppendUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!validateUpload(file, { allowWhenLoaded: true })) return;

    setIsUploading(true);
    setLoadError(null);
    try {
      const jsonData = await parseQcCsvFile(file);
      const taggedRows = jsonData.map(row => ({
        ...row,
        retailer: row.retailer && !row.retailer.startsWith(SANDBOX_UPLOAD_PREFIX)
          ? `${SANDBOX_UPLOAD_PREFIX}${row.retailer}`
          : row.retailer
      }));
      setData(prev => [...prev, ...taggedRows]);
      setHasDbData(true);
      console.log('[QCTool] SANDBOX upload appended', taggedRows.length, 'rows. No DB write.');
    } catch (error) {
      console.error('Sandbox CSV parsing error:', error);
      setLoadError(`Failed to parse CSV: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Clear database data and reset state
  const handleClearData = async () => {
    if (!confirm('Are you sure you want to clear the current data? This will remove all uploaded data from the database.')) {
      return;
    }

    setIsClearingData(true);
    const { error } = await clearQcWorkingData();
    if (error) {
      setLoadError('Failed to clear data. Please try again.');
      setIsClearingData(false);
      return;
    }

    if (SANDBOX_MODE) {
      // Wipe sandbox session — uploaded shoppers go away, seeded SANDBOX_ROWS
      // come back, marks reset. Real mode never touches sessionStorage.
      sessionStorage.removeItem(QC_DATA_KEY);
      sessionStorage.removeItem(QC_STATE_KEY);
      setData(SANDBOX_ROWS);
      setQcState({});
      setHasDbData(true);
    } else {
      // Drop the stale qcState marks for the shoppers we just cleared (scoped --
      // never a global wipe) so a later re-upload starts clean.
      const clearedKeys = collectOwnedKeys(shoppers);
      if (clearedKeys.size > 0) {
        setQcState(prev => pruneQcState(prev, clearedKeys));
      }
      setData([]);
      setHasDbData(false);
    }
    setCurrentShopperKey(null);
    setCurrentPageIndex(0);
    setActiveView('dashboard');
    setIsClearingData(false);
    console.log('[QCTool] Cleared database data');
  };

  // --- DATA PROCESSING ---
  const shoppers = useMemo(() => {
    const groups = {};
    data.forEach(item => {
      const key = `${item.retailer}-${item.startDate}-${item.endDate}`;
      if (!groups[key]) {
        groups[key] = {
          id: key,
          retailer: item.retailer,
          startDate: item.startDate,
          endDate: item.endDate,
          pages: {}
        };
      }
      if (!groups[key].pages[item.pageNumber]) {
        groups[key].pages[item.pageNumber] = {
          pageNumber: item.pageNumber,
          shopperId: item.shopperId,
          shopperImage: item.shopperImage,
          spots: {}
        };
      }
      if (!groups[key].pages[item.pageNumber].spots[item.spotId]) {
        groups[key].pages[item.pageNumber].spots[item.spotId] = {
          spotId: item.spotId,
          spotImage: item.spotImage,
          products: []
        };
      }
      groups[key].pages[item.pageNumber].spots[item.spotId].products.push(item);
    });
    return Object.values(groups);
  }, [data]);

  const retailers = useMemo(() => ['All', ...new Set(shoppers.map(s => s.retailer))], [shoppers]);
  const dates = useMemo(() => ['All', ...new Set(shoppers.map(s => s.startDate))], [shoppers]);

  const filteredShoppers = shoppers.filter(s => {
    return (selectedRetailer === 'All' || s.retailer === selectedRetailer) &&
           (selectedDate === 'All' || s.startDate === selectedDate);
  });

  const getShopperStats = (shopper) => {
    let totalSpots = 0;
    let reviewedSpots = 0;
    let errors = 0;
    let pushedErrors = 0;
    let unpushedErrors = 0;
    let totalPages = 0;
    let pushedPages = 0;
    let unpushedPagesWithMarks = 0;

    const pageNums = Object.keys(shopper.pages);
    totalPages = pageNums.length;

    // Per-page tally so we can split errors into pushed / unpushed buckets.
    // A page counts as "pushed" when qcState[page-...].exported === true.
    const errorsByPage = Object.fromEntries(pageNums.map(p => [p, 0]));

    // Page-level errors
    pageNums.forEach(pageNum => {
      const pageKey = `page-${shopper.id}-${pageNum}`;
      const pageData = qcState[pageKey];
      if (pageData?.issueType) {
        errors++;
        errorsByPage[pageNum]++;
      }
    });

    // Spot + product-level errors, attributed to the page the spot lives on.
    // Reviewed-count now comes from the shared isSpotReviewed helper so the
    // rule stays in lockstep with isPageComplete and the sidebar indicator.
    Object.entries(shopper.pages).forEach(([pageNum, page]) => {
      Object.values(page.spots).forEach(spot => {
        totalSpots++;
        const spotState = qcState[`spot-${spot.spotId}`];

        if (spot.products) {
          spot.products.forEach(product => {
            const productKey = `product-${product.upc}-spot-${spot.spotId}`;
            const productState = qcState[productKey];
            if (productState?.issueType) {
              errors++;
              errorsByPage[pageNum]++;
            }
          });
        }

        // Count a spot-level error only when an issueType is set on the spot.
        // The badge must equal what the ledger will receive — observation-only
        // and the (now-removed) X-click both produced phantom errors.
        if (spotState?.issueType) {
          errors++;
          errorsByPage[pageNum]++;
        }

        if (isSpotReviewed(spot, qcState)) reviewedSpots++;
      });
    });

    // Bucket errors by push state of the page they live on
    pageNums.forEach(pageNum => {
      const isExported = Boolean(qcState[`page-${shopper.id}-${pageNum}`]?.exported);
      const pageErrors = errorsByPage[pageNum] || 0;
      if (isExported) {
        pushedPages++;
        pushedErrors += pageErrors;
      } else {
        if (pageErrors > 0) unpushedPagesWithMarks++;
        unpushedErrors += pageErrors;
      }
    });

    return {
      totalSpots,
      reviewedSpots,
      errors,
      pushedErrors,
      unpushedErrors,
      totalPages,
      pushedPages,
      unpushedPagesWithMarks,
      percent: totalSpots ? Math.round((reviewedSpots / totalSpots) * 10) * 10 : 0,
    };
  };

  // --- ACTIONS ---
  const toggleStatus = (level, id, status) => {
    const key = `${level}-${id}`;
    
    // If trying to set status to 'pass' for a spot, check for issues
    if (level === 'spot' && status === 'pass') {
      // Check if spot has a spot-level issue
      const spotKey = `${level}-${id}`;
      const spotState = qcState[spotKey];
      if (spotState?.issueType) {
        alert('Cannot mark as correct: This spot has an issue type selected. Please clear the issue type first.');
        return;
      }
      
      // Check if any products in this spot have product-level issues
      const pageNum = currentShopper?.pages && Object.keys(currentShopper.pages)[currentPageIndex];
      if (pageNum && currentShopper) {
        const page = currentShopper.pages[pageNum];
        const spot = Object.values(page.spots).find(s => s.spotId === id);
        
        if (spot?.products) {
          const hasProductIssue = spot.products.some(product => {
            const productKey = `product-${product.upc}-spot-${id}`;
            return qcState[productKey]?.issueType;
          });
          
          if (hasProductIssue) {
            alert('Cannot mark as correct: This spot has product-level issues. Please clear all product issues first.');
            return;
          }
        }
      }
    }
    
    setQcState(prev => {
      const newState = {
        ...prev,
        [key]: { ...prev[key], status: prev[key]?.status === status ? null : status }
      };
      
      // Auto-increment counter when status is set
      if (level === 'spot' && newState[key].status && !prev[key]?.status) {
        const pageNum = currentShopper?.pages && Object.keys(currentShopper.pages)[currentPageIndex];
        if (pageNum && currentShopper) {
          const countKey = `count-${currentShopper.id}-${pageNum}`;
          const currentCount = parseInt(newState[countKey] || 0) || 0;
          newState[countKey] = currentCount + 1;
        }
      }
      // Auto-decrement counter when status is cleared
      if (level === 'spot' && !newState[key].status && prev[key]?.status) {
        const pageNum = currentShopper?.pages && Object.keys(currentShopper.pages)[currentPageIndex];
        if (pageNum && currentShopper) {
          const countKey = `count-${currentShopper.id}-${pageNum}`;
          const currentCount = parseInt(newState[countKey] || 0) || 0;
          if (currentCount > 0) {
            newState[countKey] = currentCount - 1;
          }
        }
      }
      
      return newState;
    });
  };

  const updateObservation = (level, id, text) => {
    const key = `${level}-${id}`;
    setQcState(prev => ({
      ...prev,
      [key]: { ...prev[key], observation: text }
    }));
  };

  const updateIssueType = (level, id, issueType) => {
    const key = `${level}-${id}`;
    setQcState(prev => {
      const newState = {
        ...prev,
        [key]: { ...prev[key], issueType }
      };
      
      // If an issue type is selected at spot level, automatically mark as fail
      if (level === 'spot' && issueType) {
        if (!newState[key]?.status) {
          newState[key] = { ...newState[key], status: 'fail' };
          
          // Auto-increment counter when spot is auto-failed
          const pageNum = currentShopper?.pages && Object.keys(currentShopper.pages)[currentPageIndex];
          if (pageNum && currentShopper) {
            const countKey = `count-${currentShopper.id}-${pageNum}`;
            const currentCount = parseInt(newState[countKey] || 0) || 0;
            newState[countKey] = currentCount + 1;
          }
        }
      }
      
      return newState;
    });
  };

  const updateProductIssueType = (spotId, productUpc, issueType) => {
    const key = `product-${productUpc}-spot-${spotId}`;
    setQcState(prev => {
      const newState = {
        ...prev,
        [key]: { ...prev[key], issueType }
      };
      
      // If an issue type is selected, automatically mark the spot as fail
      if (issueType) {
        const spotKey = `spot-${spotId}`;
        if (!newState[spotKey]?.status) {
          newState[spotKey] = { ...newState[spotKey], status: 'fail' };
          
          // Auto-increment counter when spot is auto-failed
          const pageNum = currentShopper?.pages && Object.keys(currentShopper.pages)[currentPageIndex];
          if (pageNum && currentShopper) {
            const countKey = `count-${currentShopper.id}-${pageNum}`;
            const currentCount = parseInt(newState[countKey] || 0) || 0;
            newState[countKey] = currentCount + 1;
          }
        }
      }
      
      return newState;
    });
  };

  const updateProductSuggestedUPC = (spotId, productUpc, suggestedUPC) => {
    const key = `product-${productUpc}-spot-${spotId}`;
    setQcState(prev => ({
      ...prev,
      [key]: { ...prev[key], suggestedUPC }
    }));
  };

  const updateProductObservation = (spotId, productUpc, observation) => {
    const key = `product-${productUpc}-spot-${spotId}`;
    setQcState(prev => ({
      ...prev,
      [key]: { ...prev[key], observation }
    }));
  };

  // Reset progress for ONLY the shopper currently in context. Every other
  // shopper's marks are preserved (fixes the global-wipe data loss), and the
  // removed slice is stashed to a recovery key so the last reset can be undone.
  const resetQCProgress = () => {
    if (!currentShopper) {
      setLoadError('Open a shopper before resetting its QC progress.');
      return;
    }

    const shopperId = currentShopper.id;

    // Reconstruct the exact qcState keys this shopper owns, mirroring how the
    // app writes them: page-/count- embed the shopper id; spot-/product- are
    // keyed by the shopper's own spot ids (globally unique per circular).
    const ownedKeys = new Set();
    Object.entries(currentShopper.pages).forEach(([pageNum, page]) => {
      ownedKeys.add(`page-${shopperId}-${pageNum}`);
      ownedKeys.add(`count-${shopperId}-${pageNum}`);
      Object.values(page.spots).forEach(spot => {
        ownedKeys.add(`spot-${spot.spotId}`);
        (spot.products || []).forEach(product => {
          ownedKeys.add(`product-${product.upc}-spot-${spot.spotId}`);
        });
      });
    });

    const label = `${currentShopper.retailer} (${currentShopper.startDate} - ${currentShopper.endDate})`;
    if (!confirm(`Reset QC progress for ${label}? This clears only this shopper's saved reviews - other shoppers are untouched. You can undo the last reset.`)) {
      return;
    }

    // Immutable split: build a NEW state object without this shopper's keys,
    // plus a separate slice of exactly what was removed (for recovery/undo).
    const removed = {};
    const next = {};
    Object.entries(qcState).forEach(([key, value]) => {
      if (ownedKeys.has(key)) {
        removed[key] = value;
      } else {
        next[key] = value;
      }
    });

    // Stash the removed slice so this reset is recoverable rather than a hard delete.
    try {
      qcStorage.setItem(
        QC_RESET_BACKUP_KEY,
        JSON.stringify({ shopperId, label, removedAt: new Date().toISOString(), state: removed })
      );
    } catch (e) {
      console.warn('[QCTool] Failed to stash reset backup', e);
    }

    // The qcState save effect persists `next` to QC_STATE_KEY automatically.
    setQcState(next);
  };

  // Undo the most recent per-shopper reset by merging the stashed slice back
  // into qcState. Immutable merge; the backup is one-shot (cleared after use).
  const undoResetProgress = () => {
    let backup = resetBackup;
    if (!backup) {
      try {
        const raw = qcStorage.getItem(QC_RESET_BACKUP_KEY);
        backup = raw ? JSON.parse(raw) : null;
      } catch (e) {
        backup = null;
      }
    }
    const removed = backup?.state;
    if (!removed || Object.keys(removed).length === 0) {
      setResetBackup(null);
      return;
    }
    setQcState(prev => ({ ...prev, ...removed }));
    try {
      qcStorage.removeItem(QC_RESET_BACKUP_KEY);
    } catch (e) {
      /* non-fatal */
    }
    setResetBackup(null);
  };

  const updateSuggestedUPC = (id, suggestedUPC) => {
    const key = `spot-${id}`;
    setQcState(prev => ({
      ...prev,
      [key]: { ...prev[key], suggestedUPC }
    }));
  };

  // --- DRAW HANDLERS ---
  // Helper: Get canvas coordinates accounting for zoom/pan on page image and object-contain positioning
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return { x: 0, y: 0 };
    
    const bounds = canvas.getBoundingClientRect();
    // Mouse position relative to canvas
    let mouseX = e.clientX - bounds.left;
    let mouseY = e.clientY - bounds.top;
    
    // Get the rendered image bounds (stored during canvas paint)
    const imgRenderWidth = parseFloat(canvas.dataset.imgRenderWidth) || canvas.width;
    const imgRenderHeight = parseFloat(canvas.dataset.imgRenderHeight) || canvas.height;
    const imgOffsetX = parseFloat(canvas.dataset.imgOffsetX) || 0;
    const imgOffsetY = parseFloat(canvas.dataset.imgOffsetY) || 0;
    
    // Account for zoom/pan transform
    // The image is scaled and translated, so we need to reverse these transforms
    const scale = pageImageZoom;
    const panX = pageImagePan.x / (scale * 100);
    const panY = pageImagePan.y / (scale * 100);
    
    // Transform mouse coords to account for image position and zoom/pan
    // First, adjust for image offset (object-contain centering)
    mouseX = mouseX - imgOffsetX;
    mouseY = mouseY - imgOffsetY;
    
    // Then account for zoom and pan
    // When zoomed, we need to reverse the scale and pan
    const imgCenterX = imgRenderWidth / 2;
    const imgCenterY = imgRenderHeight / 2;
    
    // Reverse the transform: subtract pan, divide by scale, add center offset
    mouseX = (mouseX - imgCenterX) / scale + imgCenterX - (panX * imgRenderWidth);
    mouseY = (mouseY - imgCenterY) / scale + imgCenterY - (panY * imgRenderHeight);
    
    return { x: mouseX, y: mouseY };
  };

  // Helper: Check if mouse is over a handle
  const getHandleAtPoint = (x, y, pageKey) => {
    const savedMeta = qcState[pageKey]?.rect;
    if (!savedMeta?.rect) return null;
    
    const canvas = canvasRef.current;
    const { x: rx, y: ry, w: rw, h: rh } = savedMeta.rect;
    const handleSize = 8;
    const hitBuffer = 8; // Extra pixels for easier clicking
    
    // Get rendered image dimensions
    const imgRenderWidth = parseFloat(canvas.dataset.imgRenderWidth) || canvas.width;
    const imgRenderHeight = parseFloat(canvas.dataset.imgRenderHeight) || canvas.height;
    
    const handles = [
      { type: 'nw', x: rx * imgRenderWidth, y: ry * imgRenderHeight },
      { type: 'n', x: (rx + rw/2) * imgRenderWidth, y: ry * imgRenderHeight },
      { type: 'ne', x: (rx + rw) * imgRenderWidth, y: ry * imgRenderHeight },
      { type: 'e', x: (rx + rw) * imgRenderWidth, y: (ry + rh/2) * imgRenderHeight },
      { type: 'se', x: (rx + rw) * imgRenderWidth, y: (ry + rh) * imgRenderHeight },
      { type: 's', x: (rx + rw/2) * imgRenderWidth, y: (ry + rh) * imgRenderHeight },
      { type: 'sw', x: rx * imgRenderWidth, y: (ry + rh) * imgRenderHeight },
      { type: 'w', x: rx * imgRenderWidth, y: (ry + rh/2) * imgRenderHeight },
    ];

    for (const handle of handles) {
      const dist = Math.hypot(x - handle.x, y - handle.y);
      if (dist <= handleSize/2 + hitBuffer) {
        return handle.type;
      }
    }
    return null;
  };

  const handleCanvasMouseDown = (e) => {
    const coords = getCanvasCoords(e);
    const canvas = canvasRef.current;
    const pageKey = canvas.dataset.pageKey;

    // Check if clicking on a handle to adjust existing rectangle
    const handleType = getHandleAtPoint(coords.x, coords.y, pageKey);
    if (handleType) {
      const savedMeta = qcState[pageKey]?.rect;
      if (savedMeta?.rect) {
        setDragHandle({
          type: handleType,
          startRect: { ...savedMeta.rect },
          startMouse: coords
        });
        setDrawError(null);
        return;
      }
    }

    // Otherwise, start drawing a new rectangle
    setRectDrawStart(coords);
    setIsRectDrawing(true);
    setDrawError(null);
  };

  const handleCanvasMouseMove = (e) => {
    const coords = getCanvasCoords(e);
    const canvas = canvasRef.current;
    const pageKey = canvas.dataset.pageKey;

    // Handle dragging to resize/reposition rectangle
    if (dragHandle) {
      const { type, startRect, startMouse } = dragHandle;
      // Get rendered image dimensions for delta calculation
      const imgRenderWidth = parseFloat(canvas.dataset.imgRenderWidth) || canvas.width;
      const imgRenderHeight = parseFloat(canvas.dataset.imgRenderHeight) || canvas.height;
      
      const dx = (coords.x - startMouse.x) / imgRenderWidth;
      const dy = (coords.y - startMouse.y) / imgRenderHeight;
      
      let newRect = { ...startRect };
      
      // Adjust rectangle based on which handle is being dragged
      switch (type) {
        case 'nw':
          newRect.x = startRect.x + dx;
          newRect.y = startRect.y + dy;
          newRect.w = startRect.w - dx;
          newRect.h = startRect.h - dy;
          break;
        case 'n':
          newRect.y = startRect.y + dy;
          newRect.h = startRect.h - dy;
          break;
        case 'ne':
          newRect.y = startRect.y + dy;
          newRect.w = startRect.w + dx;
          newRect.h = startRect.h - dy;
          break;
        case 'e':
          newRect.w = startRect.w + dx;
          break;
        case 'se':
          newRect.w = startRect.w + dx;
          newRect.h = startRect.h + dy;
          break;
        case 's':
          newRect.h = startRect.h + dy;
          break;
        case 'sw':
          newRect.x = startRect.x + dx;
          newRect.w = startRect.w - dx;
          newRect.h = startRect.h + dy;
          break;
        case 'w':
          newRect.x = startRect.x + dx;
          newRect.w = startRect.w - dx;
          break;
      }
      
      // Ensure minimum size and keep within bounds
      newRect.w = Math.max(0.03, Math.min(newRect.w, 1 - newRect.x));
      newRect.h = Math.max(0.03, Math.min(newRect.h, 1 - newRect.y));
      newRect.x = Math.max(0, Math.min(newRect.x, 0.97));
      newRect.y = Math.max(0, Math.min(newRect.y, 0.97));
      
      // Update the saved rect in real-time
      const img = imgRef.current;
      const meta = {
        rect: newRect,
        imgW: img.naturalWidth,
        imgH: img.naturalHeight,
        drawScale: img.getBoundingClientRect().width / img.naturalWidth,
        version: 1,
      };
      setQcState(prev => ({ ...prev, [pageKey]: { ...prev[pageKey], rect: meta } }));
      return;
    }

    // Update hovered handle for visual feedback
    if (!isRectDrawing) {
      const handleType = getHandleAtPoint(coords.x, coords.y, pageKey);
      setHoveredHandle(handleType ? { type: handleType, rectKey: pageKey } : null);
    }

    // Drawing new rectangle
    if (!isRectDrawing || !rectDrawStart) return;
    setLiveRect({ x: rectDrawStart.x, y: rectDrawStart.y, w: coords.x - rectDrawStart.x, h: coords.y - rectDrawStart.y });
  };

  const handleCanvasMouseUp = (e) => {
    // Finish handle dragging
    if (dragHandle) {
      setDragHandle(null);
      return;
    }

    // Finish drawing new rectangle
    if (!isRectDrawing || !rectDrawStart) return;
    setIsRectDrawing(false);
    
    const coords = getCanvasCoords(e);
    const canvas = canvasRef.current;
    
    // Get rendered image dimensions for normalization
    const imgRenderWidth = parseFloat(canvas.dataset.imgRenderWidth) || canvas.width;
    const imgRenderHeight = parseFloat(canvas.dataset.imgRenderHeight) || canvas.height;
    
    const xNorm = Math.min(rectDrawStart.x, coords.x) / imgRenderWidth;
    const yNorm = Math.min(rectDrawStart.y, coords.y) / imgRenderHeight;
    const wNorm = Math.abs(coords.x - rectDrawStart.x) / imgRenderWidth;
    const hNorm = Math.abs(coords.y - rectDrawStart.y) / imgRenderHeight;
    
    if (wNorm < 0.03 || hNorm < 0.03) {
      setDrawError('Rectangle too small — drag a larger area.');
      setLiveRect(null);
      return;
    }
    
    const img = imgRef.current;
    const meta = {
      rect: { x: xNorm, y: yNorm, w: wNorm, h: hNorm },
      imgW: img.naturalWidth,
      imgH: img.naturalHeight,
      drawScale: img.getBoundingClientRect().width / img.naturalWidth,
      version: 1,
    };
    const pageKey = canvas.dataset.pageKey;
    setQcState(prev => ({ ...prev, [pageKey]: { ...prev[pageKey], rect: meta } }));
    setDrawMode(false);
    setLiveRect(null);
  };

  // Helper: Get cursor style based on handle type
  const getCursorForHandle = (handleType) => {
    const cursors = {
      'nw': 'nwse-resize',
      'n': 'ns-resize',
      'ne': 'nesw-resize',
      'e': 'ew-resize',
      'se': 'nwse-resize',
      's': 'ns-resize',
      'sw': 'nesw-resize',
      'w': 'ew-resize',
    };
    return cursors[handleType] || 'default';
  };

  // --- PAGE-LEVEL PUSH ---
  // Check if all spots on a page have been reviewed. Delegates to the shared
  // helpers so this stays in lockstep with the sidebar indicator and stats.
  const isPageComplete = (shopperId, pageNum) => {
    const shopper = shoppers.find(s => s.id === shopperId);
    if (!shopper || !shopper.pages[pageNum]) return false;
    return getPageReviewStatus(shopper.pages[pageNum], qcState).status === 'complete';
  };

  const [isPushingAll, setIsPushingAll] = useState(false);

  // Push every page of a shopper that has marks and hasn't been pushed yet.
  // Sequential to keep error reporting sane and avoid hammering the ledger.
  const pushAllPendingPages = async (shopperId) => {
    if (isPushingAll) return;
    const shopper = shoppers.find(s => s.id === shopperId);
    if (!shopper) return;

    const stats = getShopperStats(shopper);
    const pendingPages = Object.keys(shopper.pages).filter(pageNum => {
      const pageKey = `page-${shopperId}-${pageNum}`;
      if (qcState[pageKey]?.exported) return false;
      // Skip pages with zero marks — nothing actionable to push.
      const hasPageMark = !!qcState[pageKey]?.issueType;
      const spotIds = Object.keys(shopper.pages[pageNum].spots);
      const hasSpotOrProductMark = spotIds.some(sid => {
        if (qcState[`spot-${sid}`]?.issueType) return true;
        const spot = shopper.pages[pageNum].spots[sid];
        return (spot.products || []).some(p =>
          qcState[`product-${p.upc}-spot-${sid}`]?.issueType
        );
      });
      return hasPageMark || hasSpotOrProductMark;
    });

    if (pendingPages.length === 0) {
      alert('No pending pages to push for this shopper.');
      return;
    }

    if (!confirm(`Push ${pendingPages.length} pending ${pendingPages.length === 1 ? 'page' : 'pages'} to the ledger for ${shopper.retailer}?`)) {
      return;
    }

    setIsPushingAll(true);
    const results = { ok: [], failed: [] };
    for (const pageNum of pendingPages) {
      const r = await pushPageToLedger(shopperId, pageNum, { silent: true });
      if (r?.ok) results.ok.push(pageNum);
      else results.failed.push({ pageNum, error: r?.error || 'unknown' });
    }
    setIsPushingAll(false);

    if (results.failed.length === 0) {
      alert(`Pushed ${results.ok.length} ${results.ok.length === 1 ? 'page' : 'pages'} (${results.ok.join(', ')}) to the ledger.`);
    } else {
      const failLines = results.failed.map(f => `  Page ${f.pageNum}: ${f.error}`).join('\n');
      alert(`Pushed ${results.ok.length} of ${pendingPages.length} pages.\nFailures:\n${failLines}`);
    }
  };

  // Push a single page to the QC ledger.
  // Returns { ok: boolean, error?: string } so batched callers can aggregate.
  // When called with { silent: true } the per-page success alert is suppressed.
  const pushPageToLedger = async (shopperId, pageNum, opts = {}) => {
    const silent = opts.silent === true;
    try {
      const sessionId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      
      const pageKey = `page-${shopperId}-${pageNum}`;
      const pageData = qcState[pageKey];
      const ledgerRows = [];
      
      // Get all items for this page
      const shopper = shoppers.find(s => s.id === shopperId);
      if (!shopper || !shopper.pages[pageNum]) return;
      
      const page = shopper.pages[pageNum];
      const pageItems = [];
      Object.values(page.spots).forEach(spot => {
        if (spot.products) {
          spot.products.forEach(product => {
            if (product.pageNumber === parseInt(pageNum)) {
              pageItems.push({ ...product, spotId: spot.spotId });
            }
          });
        }
      });
      
      if (pageItems.length === 0) return;
      
      const sampleItem = pageItems[0];
      const pageNumInt = parseInt(pageNum);
      // Page row only counts as Fail when an issueType is selected. Observation
      // alone is supporting context, not an actionable error — matches the
      // QC Tool badge semantics and the Spot/Product rules.
      const hasPageError = Boolean(pageData?.issueType);

      // Page ledger row
      ledgerRows.push({
        source: 'qc_tool',
        user_id: user?.sub ?? null,
        user_email: user?.email ?? null,
        retailer: sampleItem.retailer,
        date: sampleItem.startDate,
        page_number: pageNumInt,
        shopper_id: sampleItem.shopperId || null,
        level: 'Page',
        spot_id: null,
        upc: null,
        description: null,
        issue_type: pageData?.issueType || null,
        qc_status: hasPageError ? 'Fail' : 'Pass',
        suggested_upc_spot: null,
        observation: pageData?.observation || null,
        idempotency_key: `${sessionId}-page-${shopperId}-${pageNum}`,
        qc_session_id: sessionId,
        meta: pageData?.rect ? {
          ...pageData.rect,
          shopperImage: page.shopperImage || null
        } : (page.shopperImage ? { shopperImage: page.shopperImage } : null),
      });
      
      // Spot + product ledger rows
      const spotGroups = {};
      pageItems.forEach(item => {
        if (!spotGroups[item.spotId]) spotGroups[item.spotId] = [];
        spotGroups[item.spotId].push(item);
      });
      
      Object.entries(spotGroups).forEach(([spotId, spotProducts]) => {
        const spotKey = `spot-${spotId}`;
        const spotData = qcState[spotKey];
        // Only mark spot as Fail if it has an issueType selected (X click is just visual indicator)
        const hasSpotError = Boolean(spotData?.issueType);
        const firstProduct = spotProducts[0];
        
        // Spot ledger row
        ledgerRows.push({
          source: 'qc_tool',
          user_id: user?.sub ?? null,
          user_email: user?.email ?? null,
          retailer: firstProduct.retailer,
          date: firstProduct.startDate,
          page_number: firstProduct.pageNumber,
          shopper_id: firstProduct.shopperId || null,
          level: 'Spot',
          spot_id: spotId,
          upc: firstProduct.upc,
          description: firstProduct.description,
          issue_type: spotData?.issueType || null,
          qc_status: hasSpotError ? 'Fail' : 'Pass',
          suggested_upc_spot: spotData?.suggestedUPC || null,
          observation: spotData?.observation || null,
          idempotency_key: `${sessionId}-spot-${spotId}`,
          qc_session_id: sessionId,
          meta: null,
        });
        
        // Product ledger rows
        spotProducts.forEach(product => {
          const productKey = `product-${product.upc}-spot-${spotId}`;
          const productData = qcState[productKey];
          const hasProductError = Boolean(productData?.issueType);
          ledgerRows.push({
            source: 'qc_tool',
            user_id: user?.sub ?? null,
            user_email: user?.email ?? null,
            retailer: product.retailer,
            date: product.startDate,
            page_number: product.pageNumber,
            shopper_id: product.shopperId || null,
            level: hasProductError ? 'Product/UPC' : 'Product/UPC (Context)',
            spot_id: spotId,
            upc: product.upc,
            description: product.description,
            issue_type: productData?.issueType || null,
            qc_status: hasProductError ? 'Fail' : 'Pass',
            suggested_upc_spot: productData?.suggestedUPC || null,
            observation: productData?.observation || null,
            idempotency_key: hasProductError
              ? `${sessionId}-product-${product.upc}-spot-${spotId}`
              : `${sessionId}-ctx-${product.upc}-spot-${spotId}`,
            qc_session_id: sessionId,
            meta: null,
          });
        });
      });
      
      // Push to database
      const validLedgerRows = ledgerRows.filter(r => r.idempotency_key != null);
      if (validLedgerRows.length === 0) {
        return { ok: true, skipped: true };
      }

      const { data, error } = await bulkInsertIssues(validLedgerRows);
      if (error) {
        console.error('Failed to push page to ledger:', error);
        if (!silent) {
          alert(`Failed to save QC data to database. Error: ${error.message}\n\nPlease try again or contact support if the issue persists.`);
        }
        return { ok: false, error: error.message || 'database error' };
      }

      // Mark page as exported and figure out which other pages of this shopper
      // still have marks waiting to be pushed. We compute from `prev` so the
      // count reflects the post-update state without needing a setState round trip.
      let pendingAfter = [];
      setQcState(prev => {
        const next = {
          ...prev,
          [pageKey]: {
            ...prev[pageKey],
            exported: true,
            exportedAt: new Date().toISOString(),
            exportSessionId: sessionId,
          },
        };
        if (shopper) {
          for (const pn of Object.keys(shopper.pages)) {
            if (String(pn) === String(pageNum)) continue;
            const otherPageKey = `page-${shopperId}-${pn}`;
            if (next[otherPageKey]?.exported) continue;
            const hasPageMark = !!next[otherPageKey]?.issueType;
            const spotIds = Object.keys(shopper.pages[pn].spots);
            const hasSpotOrProductMark = spotIds.some(sid => {
              if (next[`spot-${sid}`]?.issueType) return true;
              const spot = shopper.pages[pn].spots[sid];
              return (spot.products || []).some(p =>
                next[`product-${p.upc}-spot-${sid}`]?.issueType
              );
            });
            if (hasPageMark || hasSpotOrProductMark) pendingAfter.push(pn);
          }
        }
        return next;
      });

      const insertedCount = data?.inserted?.length || 0;
      const skippedCount = data?.skipped || 0;
      if (!silent) {
        const dupNote = skippedCount > 0 ? `, ${skippedCount} duplicates skipped` : '';
        if (pendingAfter.length > 0) {
          const pageList = pendingAfter.join(', ');
          alert(
            `Page ${pageNum} pushed to QC ledger (${insertedCount} new records${dupNote}).\n\n` +
            `⚠ ${pendingAfter.length === 1 ? 'Page' : 'Pages'} ${pageList} still ${pendingAfter.length === 1 ? 'has' : 'have'} marked errors that have NOT been pushed.\n\n` +
            `Go back to the shopper dashboard and use "Push N pending pages" to ship them all at once, or open ${pendingAfter.length === 1 ? 'that page' : 'those pages'} and push individually.`
          );
        } else {
          alert(`Page ${pageNum} pushed to QC ledger successfully! (${insertedCount} new records${dupNote})\n\nAll pages with marks for this shopper have been pushed. ✓`);
        }
      }
      return { ok: true, inserted: insertedCount, skipped: skippedCount, pendingAfter };
    } catch (error) {
      console.error('Failed to push page to ledger:', error);
      if (!silent) {
        alert(`Failed to push to ledger. Error: ${error.message}\n\nPlease try again.`);
      }
      return { ok: false, error: error.message || 'unexpected error' };
    }
  };

  const downloadReport = async () => {
    if (selectedPagesForExport.length === 0) return;
    
    console.log('Export started with pages:', selectedPagesForExport);
    console.log('Current QC State:', qcState);

    const sessionId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

    // ------------------------------------------------------------------ CSV (error-only rows, unchanged behaviour)
    const reportRows = [["Retailer", "Date", "Page", "Shopper ID", "Level", "Spot ID", "UPC", "Description", "Issue Type", "Status", "Suggested UPC/Spot", "Observation"]];
    
    // Track which pages have already been added
    const processedPages = new Set();
    
    // First, add page-level failures
    selectedPagesForExport.forEach(pageIdentifier => {
      // Extract shopper ID and page number from identifier
      const lastDashIndex = pageIdentifier.lastIndexOf('-');
      const shopperId = pageIdentifier.substring(0, lastDashIndex);
      const pageNum = pageIdentifier.substring(lastDashIndex + 1);
      const pageKey = `page-${shopperId}-${pageNum}`;
      const pageData = qcState[pageKey];
      
      console.log('Checking page:', { pageIdentifier, shopperId, pageNum, pageKey, pageData });
      
      // Page-level error exists if there's an issue type or observation
      if (pageData?.issueType || pageData?.observation) {
        // Find items for this specific shopper and page
        const shopper = shoppers.find(s => s.id === shopperId);
        if (shopper) {
          const pageNumInt = parseInt(pageNum);
          const pageItems = data.filter(item => 
            item.retailer === shopper.retailer && 
            item.startDate === shopper.startDate && 
            item.pageNumber === pageNumInt
          );
          const sampleItem = pageItems[0];
          if (sampleItem) {
            reportRows.push([
              sampleItem.retailer,
              sampleItem.startDate,
              pageNum,
              sampleItem.shopperId || "",
              "Page",
              "",
              "",
              "",
              pageData?.issueType || "",
              "Fail",
              "",
              pageData?.observation || ""
            ]);
            processedPages.add(pageIdentifier);
          }
        }
      }
    });
    
    // Then, add spot-level failures and product-level issues
    // Group products by spot to provide full context
    const exportedSpots = new Set();
    selectedPagesForExport.forEach(pageIdentifier => {
      // Extract shopper ID and page number from identifier
      const lastDashIndex = pageIdentifier.lastIndexOf('-');
      const shopperId = pageIdentifier.substring(0, lastDashIndex);
      const pageNum = pageIdentifier.substring(lastDashIndex + 1);
      const shopper = shoppers.find(s => s.id === shopperId);
      if (!shopper) return;
      
      const pageNumInt = parseInt(pageNum);
      const pageItems = data.filter(item => 
        item.retailer === shopper.retailer && 
        item.startDate === shopper.startDate && 
        item.pageNumber === pageNumInt
      );
      
      // Group products by spot ID
      const spotGroups = {};
      pageItems.forEach(item => {
        if (!spotGroups[item.spotId]) {
          spotGroups[item.spotId] = [];
        }
        spotGroups[item.spotId].push(item);
      });
      
      // Process each spot
      Object.entries(spotGroups).forEach(([spotId, spotProducts]) => {
        const spotKey = `spot-${spotId}`;
        const spotData = qcState[spotKey];
        const spotExportKey = `${shopperId}-${pageNum}-${spotId}`;
        
        if (exportedSpots.has(spotExportKey)) return;
        
        // Check if spot has any errors
        const hasSpotIssueType = Boolean(spotData?.issueType);
        const hasSpotFail = spotData?.status === 'fail';
        const productIssues = spotProducts.map(product => {
          const productKey = `product-${product.upc}-spot-${spotId}`;
          return {
            product,
            issueData: qcState[productKey]
          };
        });
        const hasProductIssue = productIssues.some(pi => pi.issueData?.issueType);
        
        // Only export if spot has any error
        if (!hasSpotIssueType && !hasSpotFail && !hasProductIssue) {
          return;
        }
        
        exportedSpots.add(spotExportKey);
        
        // Export spot-level issue first (if exists)
        if (hasSpotIssueType || (hasSpotFail && !hasProductIssue)) {
          const firstProduct = spotProducts[0];
          reportRows.push([
            firstProduct.retailer,
            firstProduct.startDate,
            firstProduct.pageNumber,
            firstProduct.shopperId || "",
            "Spot",
            spotId,
            firstProduct.upc,
            firstProduct.description,
            spotData?.issueType || "",
            "Fail",
            spotData?.suggestedUPC || "",
            spotData?.observation || ""
          ]);
        }
        
        // Export ALL products from this spot for context
        // Mark which specific products have issues
        spotProducts.forEach(product => {
          const productKey = `product-${product.upc}-spot-${spotId}`;
          const productData = qcState[productKey];
          const hasProductError = Boolean(productData?.issueType);
          
          reportRows.push([
            product.retailer,
            product.startDate,
            product.pageNumber,
            product.shopperId || "",
            hasProductError ? "Product/UPC" : "Product/UPC (Context)",
            spotId,
            product.upc,
            product.description,
            productData?.issueType || "",
            hasProductError ? "Fail" : "",
            productData?.suggestedUPC || "",
            productData?.observation || ""
          ]);
        });
      });
    });

    const csvContent = reportRows.map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `QC_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // ------------------------------------------------------------------ LEDGER (all entities, including passes)
    const ledgerRows = [];
    selectedPagesForExport.forEach(pageIdentifier => {
      const lastDashIdx = pageIdentifier.lastIndexOf('-');
      const shopperId = pageIdentifier.substring(0, lastDashIdx);
      const pageNum = pageIdentifier.substring(lastDashIdx + 1);
      const pageKey = `page-${shopperId}-${pageNum}`;
      const pageData = qcState[pageKey];
      const shopper = shoppers.find(s => s.id === shopperId);
      if (!shopper) return;

      const pageNumInt = parseInt(pageNum);
      const pageItems = data.filter(item =>
        item.retailer === shopper.retailer &&
        item.startDate === shopper.startDate &&
        item.pageNumber === pageNumInt
      );
      const sampleItem = pageItems[0];
      if (!sampleItem) return;

      // Page Fail only when an issueType is selected — observation alone is
      // supporting context, matching badge semantics and the Spot/Product rule.
      const hasPageError = Boolean(pageData?.issueType);

      // Page ledger row
      ledgerRows.push({
        source: 'qc_tool',
        user_id: user?.sub ?? null,
        user_email: user?.email ?? null,
        retailer: sampleItem.retailer,
        date: sampleItem.startDate,
        page_number: pageNumInt,
        shopper_id: sampleItem.shopperId || null,
        level: 'Page',
        spot_id: null,
        upc: null,
        description: null,
        issue_type: pageData?.issueType || null,
        qc_status: hasPageError ? 'Fail' : 'Pass',
        suggested_upc_spot: null,
        observation: pageData?.observation || null,
        idempotency_key: `${sessionId}-page-${shopperId}-${pageNum}`,
        qc_session_id: sessionId,
        meta: pageData?.rect ? {
          ...pageData.rect,
          shopperImage: page.shopperImage || null
        } : (page.shopperImage ? { shopperImage: page.shopperImage } : null),
      });

      // Spot + product ledger rows
      const spotGroups = {};
      pageItems.forEach(item => {
        if (!spotGroups[item.spotId]) spotGroups[item.spotId] = [];
        spotGroups[item.spotId].push(item);
      });

      Object.entries(spotGroups).forEach(([spotId, spotProducts]) => {
        const spotKey = `spot-${spotId}`;
        const spotData = qcState[spotKey];
        // Only mark spot as Fail if it has an issueType selected (X click is just visual indicator)
        const hasSpotError = Boolean(spotData?.issueType);
        const firstProduct = spotProducts[0];

        // Spot ledger row
        ledgerRows.push({
          source: 'qc_tool',
          user_id: user?.sub ?? null,
          user_email: user?.email ?? null,
          retailer: firstProduct.retailer,
          date: firstProduct.startDate,
          page_number: firstProduct.pageNumber,
          shopper_id: firstProduct.shopperId || null,
          level: 'Spot',
          spot_id: spotId,
          upc: firstProduct.upc,
          description: firstProduct.description,
          issue_type: spotData?.issueType || null,
          qc_status: hasSpotError ? 'Fail' : 'Pass',
          suggested_upc_spot: spotData?.suggestedUPC || null,
          observation: spotData?.observation || null,
          idempotency_key: `${sessionId}-spot-${spotId}`,
          qc_session_id: sessionId,
          meta: null,
        });

        // Product ledger rows
        spotProducts.forEach(product => {
          const productKey = `product-${product.upc}-spot-${spotId}`;
          const productData = qcState[productKey];
          const hasProductError = Boolean(productData?.issueType);
          ledgerRows.push({
            source: 'qc_tool',
            user_id: user?.sub ?? null,
            user_email: user?.email ?? null,
            retailer: product.retailer,
            date: product.startDate,
            page_number: product.pageNumber,
            shopper_id: product.shopperId || null,
            level: hasProductError ? 'Product/UPC' : 'Product/UPC (Context)',
            spot_id: spotId,
            upc: product.upc,
            description: product.description,
            issue_type: productData?.issueType || null,
            qc_status: hasProductError ? 'Fail' : 'Pass',
            suggested_upc_spot: productData?.suggestedUPC || null,
            observation: productData?.observation || null,
            idempotency_key: hasProductError
              ? `${sessionId}-product-${product.upc}-spot-${spotId}`
              : `${sessionId}-ctx-${product.upc}-spot-${spotId}`,
            qc_session_id: sessionId,
            meta: null,
          });
        });
      });
    });

    // Bulk-insert all ledger rows to database
    const validLedgerRows = ledgerRows.filter(r => r.idempotency_key != null);
    if (validLedgerRows.length > 0) {
      try {
        const { data, error } = await bulkInsertIssues(validLedgerRows);

        if (error) {
          console.error('Failed to save QC data to database:', error);
          alert(`Warning: QC data could not be saved to the database.\n\nError: ${error.message}\n\nThe CSV file was downloaded, but records were NOT saved. Please contact support.`);
          // Don't mark as exported since database save failed
          setShowExportModal(false);
          setSelectedPagesForExport([]);
          return;
        }

        const insertedCount = data?.inserted?.length || 0;
        const skippedCount = data?.skipped || 0;
        console.log(`Database save successful: ${insertedCount} inserted, ${skippedCount} skipped`);

        // Mark selected pages as exported (only if database save succeeded)
        setQcState(prev => {
          const newState = { ...prev };
          selectedPagesForExport.forEach(pageIdentifier => {
            const lastDashIdx = pageIdentifier.lastIndexOf('-');
            const shopperId = pageIdentifier.substring(0, lastDashIdx);
            const pNum = pageIdentifier.substring(lastDashIdx + 1);
            const pageKey = `page-${shopperId}-${pNum}`;
            newState[pageKey] = { ...newState[pageKey], exported: true };
          });
          return newState;
        });
      } catch (err) {
        console.error('Unexpected error during database save:', err);
        alert(`Warning: An unexpected error occurred while saving QC data.\n\nError: ${err.message}\n\nPlease contact support.`);
        setShowExportModal(false);
        setSelectedPagesForExport([]);
        return;
      }
    }

    // Close modal and clear selection
    setShowExportModal(false);
    setSelectedPagesForExport([]);
  };

  // --- RENDER HELPERS ---
  const currentShopper = shoppers.find(s => s.id === currentShopperKey);

  // Calculate total errors for current shopper (only when in QC view)
  const totalErrors = useMemo(() => {
    if (!currentShopper || activeView !== 'qc') return 0;
    
    let errorCount = 0;
    const pageNumbers = Object.keys(currentShopper.pages).sort((a, b) => a - b);
    
    // Count page-level errors (only count if there's an issue type)
    pageNumbers.forEach(p => {
      const pageKey = `page-${currentShopper.id}-${p}`;
      if (qcState[pageKey]?.issueType) {
        errorCount++;
      }
    });
    
    // Count spot-level errors and product-level errors
    Object.values(currentShopper.pages).forEach(page => {
      Object.values(page.spots).forEach(spot => {
        const spotKey = `spot-${spot.spotId}`;
        
        // Check if spot has any product-level errors
        let hasProductError = false;
        if (spot.products) {
          spot.products.forEach(product => {
            const productKey = `product-${product.upc}-spot-${spot.spotId}`;
            if (qcState[productKey]?.issueType) {
              hasProductError = true;
              errorCount++;
            }
          });
        }
        
        const hasSpotIssueType = Boolean(qcState[spotKey]?.issueType);

        // Count spot-level issue only when there is an actionable signal.
        // status === 'fail' without an issueType is no longer user-creatable,
        // and treating it as an error here would count legacy orphans.
        if (hasSpotIssueType) {
          errorCount++;
        }
      });
    });

    return errorCount;
  }, [currentShopper, activeView, qcState]);

  if (activeView === 'dashboard') {
    return (
      <div className="min-h-screen bg-neutral-50 p-6 font-sans text-neutral-900">
        <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-800">QC Dashboard</h1>
            <p className="text-neutral-500">Track data accuracy and shopper integrity</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => setShowExportModal(true)}
              disabled={data.length === 0}
              className="flex items-center gap-2 bg-white border border-neutral-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-neutral-50 disabled:opacity-50"
            >
              <Download size={18} /> Export Errors
            </button>
            <button
              onClick={resetQCProgress}
              disabled={Object.keys(qcState).length === 0}
              className="flex items-center gap-2 bg-white border border-error-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-error-50 text-error-600 disabled:opacity-50"
            >
              <Trash2 size={18} /> Reset Progress
            </button>
            {resetBackup && (
              <button
                onClick={undoResetProgress}
                title={`Restore the reviews cleared from ${resetBackup.label || 'the last reset'}`}
                className="flex items-center gap-2 bg-white border border-primary-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary-50 text-primary-600"
              >
                <RotateCcw size={18} /> Undo Reset
              </button>
            )}
            {SANDBOX_MODE && hasDbData && (
              <label className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm bg-sky-600 text-white hover:bg-sky-700 cursor-pointer">
                <Upload size={18} /> {isUploading ? 'Adding...' : 'Add Sandbox Shopper'}
                <input
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={handleSandboxAppendUpload}
                  disabled={isUploading}
                />
              </label>
            )}
            {hasDbData ? (
              <button
                onClick={handleClearData}
                disabled={isClearingData}
                className="flex items-center gap-2 bg-white border border-warning-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-warning-50 text-warning-600 disabled:opacity-50"
              >
                <Trash2 size={18} /> {isClearingData ? 'Clearing...' : 'Clear & Upload New'}
              </button>
            ) : (
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm ${
                isLoadingDb
                  ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700 cursor-pointer'
              }`}>
                <Upload size={18} /> {isUploading ? 'Uploading...' : isLoadingDb ? 'Loading...' : 'Upload Data'}
                <input
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={isLoadingDb}
                />
              </label>
            )}
          </div>
        </header>

        {/* Error Display */}
        {loadError && (
          <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-error-600 flex-shrink-0" size={20} />
            <div>
              <p className="text-error-800 font-medium">Upload Error</p>
              <p className="text-error-700 text-sm">{loadError}</p>
            </div>
            <button 
              onClick={() => setLoadError(null)}
              className="ml-auto text-error-600 hover:text-error-800"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {isLoadingDb ? (
          <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-neutral-200 rounded-2xl bg-white">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mb-4"></div>
            <p className="text-neutral-400 font-medium">Loading QC data...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-neutral-200 rounded-2xl bg-white">
            <Layout size={48} className="text-neutral-200 mb-4" />
            <p className="text-neutral-400 font-medium">No data uploaded yet. Please upload a QC CSV to begin.</p>
          </div>
        ) : (
          <>
            <div className="flex gap-4 mb-6">
              <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-lg px-3 py-2 shadow-sm">
                <Filter size={18} className="text-neutral-400" />
                <select className="outline-none bg-transparent text-sm" value={selectedRetailer} onChange={(e) => setSelectedRetailer(e.target.value)}>
                  {retailers.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-lg px-3 py-2 shadow-sm">
                <select className="outline-none bg-transparent text-sm" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>
                  {dates.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredShoppers.map((shopper) => {
                const stats = getShopperStats(shopper);
                return (
                  <div key={shopper.id} className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
                    <div className="p-5">
                      <div className="flex justify-between items-start mb-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-lg truncate">{shopper.retailer}</h3>
                            {isSandboxRetailer(shopper.retailer) && (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">
                                Sandbox
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-neutral-500">{shopper.startDate} — {shopper.endDate}</p>
                        </div>
                        {stats.errors > 0 && (
                          <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                            <div className="flex items-center gap-1 text-error-500 bg-error-50 px-2 py-1 rounded text-[10px] font-bold">
                              <AlertCircle size={12} /> {stats.errors} ERRORS
                            </div>
                            {stats.unpushedErrors > 0 && (
                              <div className="flex items-center gap-1 text-warning-700 bg-warning-50 px-2 py-1 rounded text-[10px] font-bold whitespace-nowrap">
                                <Upload size={10} /> {stats.unpushedErrors} UNPUSHED
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between text-xs font-semibold">
                          <span className="text-neutral-400">QC PROGRESS</span>
                          <span className="text-neutral-700">{stats.reviewedSpots} / {stats.totalSpots} Spots</span>
                        </div>
                        <div className="w-full bg-neutral-100 h-2 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${stats.percent === 100 ? 'bg-success-500' : 'bg-primary-500'}`}
                            style={{ width: `${(stats.reviewedSpots / stats.totalSpots) * 100}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between text-[10px] font-semibold mb-4">
                        <span className="text-neutral-400 uppercase tracking-wider">Pages pushed</span>
                        <span className={cn(
                          stats.pushedPages === stats.totalPages ? 'text-success-700' : 'text-warning-700'
                        )}>
                          {stats.pushedPages} / {stats.totalPages}
                          {stats.unpushedPagesWithMarks > 0 && (
                            <span className="ml-1 text-neutral-500">({stats.unpushedPagesWithMarks} with marks)</span>
                          )}
                        </span>
                      </div>

                      {stats.unpushedPagesWithMarks > 0 && (
                        <button
                          onClick={() => pushAllPendingPages(shopper.id)}
                          disabled={isPushingAll}
                          className={cn(
                            "w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm mb-2 transition-colors",
                            isPushingAll
                              ? "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                              : "bg-warning-500 text-white hover:bg-warning-600"
                          )}
                        >
                          <Upload size={16} />
                          {isPushingAll
                            ? 'Pushing...'
                            : `Push ${stats.unpushedPagesWithMarks} pending ${stats.unpushedPagesWithMarks === 1 ? 'page' : 'pages'}`}
                        </button>
                      )}

                      <button
                        onClick={() => { setCurrentShopperKey(shopper.id); setActiveView('qc'); }}
                        className="w-full flex items-center justify-center gap-2 bg-neutral-900 text-white py-2.5 rounded-lg font-medium hover:bg-neutral-800 transition-colors"
                      >
                        <Eye size={18} /> {stats.reviewedSpots === 0 ? 'Start Review' : 'Continue Review'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-auto">
              <div className="p-6 border-b border-neutral-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-neutral-800">Export Error Report</h3>
                  <button
                    onClick={() => {
                      setShowExportModal(false);
                      setSelectedPagesForExport([]);
                    }}
                    className="text-neutral-400 hover:text-neutral-600"
                  >
                    <X size={20} />
                  </button>
                </div>
                <p className="text-sm text-neutral-500 mt-2">Select completed pages to export errors. Already exported pages are marked.</p>
              </div>
              
              <div className="p-6">
                {shoppers.map(shopper => {
                  const pageNumbers = Object.keys(shopper.pages).sort((a, b) => a - b);
                  const completedPages = pageNumbers.filter(p => {
                    const pageSpots = Object.values(shopper.pages[p].spots);
                    const countKey = `count-${shopper.id}-${p}`;
                    return (qcState[countKey] || 0) == pageSpots.length;
                  });
                  
                  if (completedPages.length === 0) return null;
                  
                  return (
                    <div key={shopper.id} className="mb-6">
                      <h4 className="font-bold text-neutral-700 mb-3">{shopper.retailer} - {shopper.startDate}</h4>
                      <div className="grid grid-cols-4 gap-2">
                        {completedPages.map(pageNum => {
                          const pageKey = `page-${shopper.id}-${pageNum}`;
                          const isExported = qcState[pageKey]?.exported;
                          const pageIdentifier = `${shopper.id}-${pageNum}`;
                          const isSelected = selectedPagesForExport.includes(pageIdentifier);
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setSelectedPagesForExport(prev => 
                                  prev.includes(pageIdentifier)
                                    ? prev.filter(p => p !== pageIdentifier)
                                    : [...prev, pageIdentifier]
                                );
                              }}
                              className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                                  : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>Page {pageNum}</span>
                                {isExported && (
                                  <span className="text-[10px] bg-success-100 text-success-600 px-1.5 py-0.5 rounded">Exported</span>
                                )}
                                {isSelected && (
                                  <CheckCircle2 size={16} className="text-primary-600" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="p-6 border-t border-neutral-200 flex justify-between items-center">
                <span className="text-sm text-neutral-600">
                  {selectedPagesForExport.length} page{selectedPagesForExport.length !== 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowExportModal(false);
                      setSelectedPagesForExport([]);
                    }}
                    className="px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={downloadReport}
                    disabled={selectedPagesForExport.length === 0}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Download size={16} />
                    Export Selected
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // QC VIEW LOGIC
  if (!currentShopper) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-100">
        <div className="text-center">
          <p className="text-neutral-500">No shopper selected</p>
          <button 
            onClick={() => setActiveView('dashboard')}
            className="mt-4 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // New Product Form handlers
  const handleNewProductChange = (field, value) => {
    setNewProduct(prev => ({ ...prev, [field]: value }));
    // Typing in the field is the user acting on the complaint — drop it and
    // re-check on blur, so the message doesn't nag mid-edit.
    if (field === 'spotNumberLink') setSpotLinkError(null);
  };

  const handleSpotLinkBlur = () => {
    const { error } = validateSpotLink(newProduct.spotNumberLink);
    setSpotLinkError(error);
  };

  const saveNewProduct = async () => {
    if (isSavingNewProduct) return;

    // Validation
    if (!newProduct.varietyType) {
      setNewProductError('Variety Type is required. Please select an option.');
      return;
    }

    // Spot Link stays optional, but anything typed in it has to be a real spot
    // link — a malformed one is worse than a blank, since New Products Requests
    // renders it as a dead link and can't look the spot up.
    const spotLink = validateSpotLink(newProduct.spotNumberLink);
    if (!spotLink.valid) {
      setSpotLinkError(spotLink.error);
      return;
    }
    setSpotLinkError(null);

    setIsSavingNewProduct(true);
    setNewProductError(null);

    const issuePayload = {
      source: 'pm_new_product',
      // Normalized, so every stored link is the canonical form regardless of
      // which variant was pasted.
      spot_number_link: spotLink.normalized,
      suggested_description: newProduct.suggestedDescription ?? '',
      reference_link: newProduct.referenceLink ?? '',
      level: newProduct.varietyType, // Store variety type in level column
      user_id: user?.sub ?? null,
      user_email: user?.email ?? null,
    };

    const { error } = await insertIssue(issuePayload);

    if (error) {
      setNewProductError('Could not create product request. Please try again.');
      setIsSavingNewProduct(false);
      return;
    }

    const productToAdd = {
      ...newProduct,
      _originalIndex: newProducts.length
    };
    setNewProducts(prev => [...prev, productToAdd]); // Store in separate state

    setIsSavingNewProduct(false);
    setShowNewProductForm(false);
    setNewProduct({});
  };

  const pageNumbers = Object.keys(currentShopper.pages).sort((a, b) => a - b);
  const pageNum = pageNumbers[currentPageIndex];
  const page = currentShopper.pages[pageNum];
  const spots = Object.values(page.spots);

  return (
    <div className="flex h-screen bg-neutral-100 font-sans">
      {/* Sidebar */}
      <aside className={`bg-white border-r border-neutral-200 flex flex-col shadow-xl z-10 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'w-0' : 'w-80'}`}>
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between bg-neutral-900 text-white">
          <button onClick={() => setActiveView('dashboard')} className="hover:text-primary-400 transition-colors" title="Back to QC List">
            <ChevronLeft size={24} />
          </button>
          <div className="text-center">
            <h2 className="font-bold text-xs uppercase tracking-widest">{currentShopper.retailer}</h2>
            <p className="text-[10px] opacity-60">P.{pageNum} of {pageNumbers.length}</p>
          </div>
          <div className="w-6" />
        </div>

        {/* Error Counter */}
        {totalErrors > 0 && (
          <button
            onClick={() => setShowErrorSummary(true)}
            className="w-full px-4 py-3 bg-error-50 border-b border-error-100 hover:bg-error-100 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-error-500" />
                <span className="text-xs font-bold text-error-700 uppercase tracking-wide">Total Errors</span>
              </div>
              <div className="bg-error-500 text-white px-3 py-1 rounded-full text-sm font-black">
                {totalErrors}
              </div>
            </div>
          </button>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {pageNumbers.map((p, idx) => {
            const pageId = `page-${currentShopper.id}-${p}`;
            // Check for page-level errors, spot-level errors, or product-level errors
            const hasPageError = qcState[pageId]?.issueType;
            const hasSpotError = Object.keys(currentShopper.pages[p].spots).some(sid => {
              const spotKey = `spot-${sid}`;
              if (qcState[spotKey]?.issueType) return true;
              const spot = currentShopper.pages[p].spots[sid];
              if (spot.products) {
                return spot.products.some(product => {
                  const productKey = `product-${product.upc}-spot-${sid}`;
                  return qcState[productKey]?.issueType;
                });
              }
              return false;
            });
            const hasError = hasPageError || hasSpotError;

            // Tri-state review status — this is the "did I skip any spots on
            // this page?" signal QCs need while scrolling through 10 pages.
            const {
              reviewed,
              total,
              status: reviewStatus,
            } = getPageReviewStatus(currentShopper.pages[p], qcState);
            const isSelected = currentPageIndex === idx;

            // Left-border accent + subtle bg tint — scanning the sidebar gives
            // a color band: green = done, yellow = partial, gray = not started.
            const bandClass = isSelected
              ? 'bg-primary-50 border-primary-200 text-primary-700 shadow-sm'
              : reviewStatus === 'complete'
                ? 'bg-success-50 border-l-4 border-l-success-500 border-y-neutral-100 border-r-neutral-100 text-neutral-700 hover:bg-success-100'
                : reviewStatus === 'partial'
                  ? 'bg-warning-50 border-l-4 border-l-warning-500 border-y-neutral-100 border-r-neutral-100 text-neutral-700 hover:bg-warning-100'
                  : 'bg-white border-neutral-100 text-neutral-500 hover:bg-neutral-50';

            const StatusGlyph = reviewStatus === 'complete'
              ? <CheckCircle2 size={16} className="text-success-500" />
              : reviewStatus === 'partial'
                ? <Circle size={16} className="text-warning-500 fill-warning-100" />
                : <Circle size={16} className="text-neutral-300" />;

            const statusTitle = reviewStatus === 'complete'
              ? `All ${total} spots reviewed`
              : reviewStatus === 'partial'
                ? `${reviewed} of ${total} spots reviewed — some skipped`
                : `Not started (${total} spots)`;

            return (
              <button
                key={p}
                onClick={() => setCurrentPageIndex(idx)}
                title={statusTitle}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all border ${bandClass}`}
              >
                <div className={`w-7 h-7 rounded flex items-center justify-center text-[10px] font-bold ${
                  isSelected ? 'bg-primary-600 text-white' : 'bg-neutral-100'
                }`}>
                  {p}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold leading-tight">Page {p}</div>
                  <div className="text-[10px] text-neutral-500 leading-tight">
                    {reviewed}/{total} spots
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {hasError && <AlertCircle size={14} className="text-error-500" />}
                  {qcState[pageId]?.observation && <MessageSquare size={14} className="text-primary-400" />}
                </div>
                {StatusGlyph}
              </button>
            );
          })}
        </div>

        <div className="p-4 bg-neutral-50 border-t border-neutral-200">
          <div className="mb-4 pb-3 border-b border-neutral-200">
            <div className="text-[10px] font-black text-neutral-400 mb-1 uppercase">Shopper ID</div>
            <div className="text-sm font-bold text-neutral-700">{page.shopperId || 'N/A'}</div>
          </div>
          <div className="mb-3">
            <label className="text-[10px] font-bold text-neutral-400 block mb-1 uppercase">Issue Type</label>
            <select
              className="w-full text-xs border border-neutral-200 rounded p-2 outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              value={qcState[`page-${currentShopper.id}-${pageNum}`]?.issueType || ''}
              onChange={(e) => updateIssueType('page', `${currentShopper.id}-${pageNum}`, e.target.value)}
            >
              <option value="">Select issue...</option>
              <option value="Missing Spot">Missing Spot</option>
              <option value="Wrong Date(s)">Wrong Date(s)</option>
              <option value="Wrong Retailer">Wrong Retailer</option>
            </select>
          </div>
          {qcState[`page-${currentShopper.id}-${pageNum}`]?.issueType === 'Missing Spot' && (
            <div className="mb-3 flex gap-2">
              <button
                onClick={() => { setDrawMode(true); setDrawError(null); }}
                className="flex-1 text-xs bg-primary-600 text-white rounded px-2 py-1.5 font-semibold hover:bg-primary-700"
              >
                Mark Missing Spot Location
              </button>
              {qcState[`page-${currentShopper.id}-${pageNum}`]?.rect && (
                <button
                  onClick={() => setQcState(prev => {
                    const key = `page-${currentShopper.id}-${pageNum}`;
                    const updated = { ...prev[key] };
                    delete updated.rect;
                    return { ...prev, [key]: updated };
                  })}
                  className="text-xs bg-neutral-200 text-neutral-700 rounded px-2 py-1.5 font-semibold hover:bg-neutral-300"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          <div className="mb-4">
            <label className="text-[10px] font-bold text-neutral-400 block mb-1 uppercase">Page Observation</label>
            <textarea 
              className="w-full text-xs border border-neutral-200 rounded p-2 h-16 outline-none focus:ring-2 focus:ring-primary-500 resize-none shadow-inner"
              placeholder="e.g. Missing logo, misprint..."
              value={qcState[`page-${currentShopper.id}-${pageNum}`]?.observation || ''}
              onChange={(e) => updateObservation('page', `${currentShopper.id}-${pageNum}`, e.target.value)}
            />
          </div>

          {/* Page Completion Status */}
          {(() => {
            const { reviewed, total, ratio, status } = getPageReviewStatus(page, qcState);
            const isComplete = status === 'complete';
            const progressPercent = ratio * 100;

            return (
              <div className="mb-3 p-2.5 rounded bg-neutral-50 border border-neutral-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-neutral-500 uppercase">QC Progress</span>
                  <span className="text-xs font-semibold text-neutral-700">
                    {reviewed} / {total} spots
                  </span>
                </div>
                <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${isComplete ? 'bg-success-500' : 'bg-primary-500'}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                {isComplete && (
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-success-600 font-semibold">
                    <CheckCircle2 size={12} />
                    <span>All spots reviewed</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Push to Ledger Button */}
          {(() => {
            const pageKey = `page-${currentShopper.id}-${pageNum}`;
            const isComplete = isPageComplete(currentShopper.id, pageNum);
            const isExported = qcState[pageKey]?.exported;
            const exportedAt = qcState[pageKey]?.exportedAt;

            return (
              <div className="mb-3">
                <button
                  onClick={() => pushPageToLedger(currentShopper.id, pageNum)}
                  disabled={!isComplete || isExported}
                  className={`w-full py-2.5 rounded font-bold text-xs shadow transition-all flex items-center justify-center gap-2 ${
                    isExported
                      ? 'bg-success-100 text-success-700 cursor-not-allowed border border-success-200'
                      : isComplete
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                  }`}
                >
                  {isExported ? (
                    <>
                      <Check size={14} />
                      Pushed to Ledger
                    </>
                  ) : isComplete ? (
                    <>
                      <Upload size={14} />
                      Push Page to Ledger
                    </>
                  ) : (
                    'Complete All Spots to Push'
                  )}
                </button>
                
                {isExported && exportedAt && (
                  <div className="mt-2 text-[10px] text-neutral-500 text-center">
                    Exported {new Date(exportedAt).toLocaleString()}
                  </div>
                )}
              </div>
            );
          })()}

          <button 
            onClick={() => setCurrentPageIndex(prev => Math.min(pageNumbers.length - 1, prev + 1))}
            className="w-full bg-neutral-900 text-white py-2 rounded font-bold text-xs shadow hover:bg-neutral-800"
          >
            Next Page
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <main className="flex-1 overflow-hidden flex flex-col">
        <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center gap-6">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 hover:bg-neutral-100 rounded text-neutral-500 hover:text-neutral-700 transition-colors"
              title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}
            >
              <ChevronLeft size={20} style={{ transform: sidebarCollapsed ? 'scaleX(-1)' : 'scaleX(1)' }} />
            </button>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-neutral-400 font-bold uppercase tracking-wider">Spot Count:</span>
              <div className="flex items-center bg-neutral-100 rounded-full px-3 py-1 font-bold">
                <input
                  type="number"
                  className="w-8 bg-transparent text-center border-none outline-none text-primary-600"
                  value={qcState[`count-${currentShopper.id}-${pageNum}`] || 0}
                  readOnly
                />
                <span className="text-neutral-300 mx-1">/</span>
                <span>{spots.length}</span>
              </div>
              {(qcState[`count-${currentShopper.id}-${pageNum}`] || 0) != spots.length ? (
                <span className="animate-pulse bg-error-100 text-error-600 px-2 py-0.5 rounded text-[10px] font-black uppercase">Count Mismatch</span>
              ) : (
                <span className="bg-success-100 text-success-600 px-2 py-0.5 rounded text-[10px] font-black uppercase">✓ Complete</span>
              )}
              <span className="text-neutral-400 font-bold uppercase tracking-wider ml-2">Products:</span>
              <div className="flex items-center bg-neutral-100 rounded-full px-3 py-1 font-bold text-primary-600">
                {spots.reduce((sum, s) => sum + (s.products?.length || 0), 0)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const prefilled = {
                  spotNumberLink: '',
                  suggestedDescription: '',
                  referenceLink: '',
                  varietyType: ''
                };
                setNewProductError(null);
                setSpotLinkError(null);
                setIsSavingNewProduct(false);
                setNewProduct(prefilled);
                setShowNewProductForm(true);
              }}
              className="flex items-center gap-2 bg-success-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-success-700 transition-colors shadow-sm"
            >
              <Plus size={16} /> Create New Product
            </button>
            <button onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))} className="p-1.5 hover:bg-neutral-100 rounded text-neutral-500"><ChevronLeft/></button>
            <button onClick={() => setCurrentPageIndex(prev => Math.min(pageNumbers.length - 1, prev + 1))} className="p-1.5 hover:bg-neutral-100 rounded text-neutral-500"><ChevronRight/></button>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Center Panel - Page or Spot Image */}
          <div className="flex-1 bg-neutral-200 relative p-4 overflow-hidden flex flex-col">
            {/* Image Navigation Controls - Always Available */}
            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-2 z-10 flex gap-2">
              {selectedSpotImage ? (
                <>
                  <button
                    onClick={() => setImageZoom(prev => Math.min(prev + 0.2, 5))}
                    className="px-3 py-1 bg-primary-500 text-white rounded hover:bg-primary-600 text-sm font-semibold"
                    title="Zoom In (Scroll Up)"
                  >
                    +
                  </button>
                  <span className="px-3 py-1 bg-neutral-100 rounded text-sm font-semibold min-w-[60px] text-center">
                    {Math.round(imageZoom * 100)}%
                  </span>
                  <button
                    onClick={() => setImageZoom(prev => Math.max(prev - 0.2, 0.5))}
                    className="px-3 py-1 bg-primary-500 text-white rounded hover:bg-primary-600 text-sm font-semibold"
                    title="Zoom Out (Scroll Down)"
                  >
                    −
                  </button>
                  <button
                    onClick={() => {
                      setImageZoom(1);
                      setImagePan({ x: 0, y: 0 });
                    }}
                    className="px-3 py-1 bg-neutral-500 text-white rounded hover:bg-neutral-600 text-sm font-semibold"
                    title="Reset Zoom"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSpotImage(null);
                      setImageZoom(1);
                      setImagePan({ x: 0, y: 0 });
                    }}
                    className="px-3 py-1 bg-error-500 text-white rounded hover:bg-error-600 text-sm font-semibold"
                    title="Close Image"
                  >
                    ✕
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setPageImageZoom(prev => Math.min(prev + 0.2, 5))}
                    className="px-3 py-1 bg-primary-500 text-white rounded hover:bg-primary-600 text-sm font-semibold"
                    title="Zoom In (Scroll Up)"
                  >
                    +
                  </button>
                  <span className="px-3 py-1 bg-neutral-100 rounded text-sm font-semibold min-w-[60px] text-center">
                    {Math.round(pageImageZoom * 100)}%
                  </span>
                  <button
                    onClick={() => setPageImageZoom(prev => Math.max(prev - 0.2, 0.5))}
                    className="px-3 py-1 bg-primary-500 text-white rounded hover:bg-primary-600 text-sm font-semibold"
                    title="Zoom Out (Scroll Down)"
                  >
                    −
                  </button>
                  <button
                    onClick={() => {
                      setPageImageZoom(1);
                      setPageImagePan({ x: 0, y: 0 });
                    }}
                    className="px-3 py-1 bg-neutral-500 text-white rounded hover:bg-neutral-600 text-sm font-semibold"
                    title="Reset Zoom (Tip: Hold Spacebar to pan while zoomed)"
                  >
                    Reset
                  </button>
                </>
              )}
            </div>

            {selectedSpotImage ? (
              <div
                className="flex-1 overflow-hidden flex items-center justify-center"
                onMouseDown={(e) => {
                  if (imageZoom > 1) {
                    setIsDragging(true);
                    setDragStart({ x: e.clientX - imagePan.x, y: e.clientY - imagePan.y });
                  }
                }}
                onMouseMove={(e) => {
                  if (isDragging && imageZoom > 1) {
                    setImagePan({
                      x: e.clientX - dragStart.x,
                      y: e.clientY - dragStart.y
                    });
                  }
                }}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? -0.1 : 0.1;
                  setImageZoom(prev => Math.max(0.5, Math.min(prev + delta, 5)));
                }}
                style={{ cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'auto' }}
              >
                <img
                  src={selectedSpotImage}
                  className="shadow-2xl rounded border-4 border-white select-none"
                  style={{
                    maxHeight: '100%',
                    maxWidth: '100%',
                    transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
                    transition: isDragging ? 'none' : 'transform 0.1s'
                  }}
                />
              </div>
            ) : (
              <div 
                className="flex-1 bg-neutral-200 overflow-hidden relative flex items-center justify-center"
                onMouseDown={(e) => {
                  // Enable pan when: spacebar held OR (not in draw mode AND zoomed)
                  if ((spacebarHeld || !drawMode) && pageImageZoom > 1) {
                    setIsPageDragging(true);
                    setPageDragStart({ x: e.clientX - pageImagePan.x, y: e.clientY - pageImagePan.y });
                  }
                }}
                onMouseMove={(e) => {
                  if (isPageDragging && pageImageZoom > 1) {
                    setPageImagePan({
                      x: e.clientX - pageDragStart.x,
                      y: e.clientY - pageDragStart.y
                    });
                  }
                }}
                onMouseUp={() => setIsPageDragging(false)}
                onMouseLeave={() => setIsPageDragging(false)}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? -0.1 : 0.1;
                  setPageImageZoom(prev => Math.max(0.5, Math.min(prev + delta, 5)));
                }}
                style={{ 
                  cursor: spacebarHeld
                    ? (isPageDragging ? 'grabbing' : 'grab')
                    : drawMode 
                      ? 'default'
                      : pageImageZoom > 1 
                        ? (isPageDragging ? 'grabbing' : 'grab') 
                        : 'auto' 
                }}
              >
                {drawMode && (
                  <div className="absolute top-4 left-0 right-0 z-20 flex justify-center pointer-events-none">
                    <span className="bg-primary-600 text-white text-[11px] px-2 py-1 rounded-b shadow">
                      Click and drag to mark the missing spot location • Hold Spacebar to pan
                    </span>
                  </div>
                )}
                
                {drawError && (
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center z-20 pointer-events-none">
                    <span className="bg-error-600 text-white text-[11px] px-3 py-1 rounded shadow">
                      {drawError}
                    </span>
                  </div>
                )}

                <img
                  ref={imgRef}
                  src={page.shopperImage}
                  className="max-h-full max-w-full object-contain shadow-2xl rounded border-4 border-white select-none"
                  style={{ 
                    display: 'block',
                    transform: `scale(${pageImageZoom}) translate(${pageImagePan.x / pageImageZoom}px, ${pageImagePan.y / pageImageZoom}px)`,
                    transition: isPageDragging ? 'none' : 'transform 0.1s',
                    outline: drawMode ? '2px dashed rgb(96, 165, 250)' : 'none',
                    outlineOffset: drawMode ? '8px' : '0'
                  }}
                />
                
                <canvas
                  ref={canvasRef}
                  data-page-key={`page-${currentShopper.id}-${pageNum}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    transform: `scale(${pageImageZoom}) translate(${pageImagePan.x / pageImageZoom}px, ${pageImagePan.y / pageImageZoom}px)`,
                    transition: isPageDragging ? 'none' : 'transform 0.1s',
                    cursor: spacebarHeld
                      ? (isPageDragging ? 'grabbing' : 'grab')
                      : dragHandle 
                        ? getCursorForHandle(dragHandle.type)
                        : hoveredHandle 
                          ? getCursorForHandle(hoveredHandle.type)
                          : drawMode 
                            ? 'crosshair' 
                            : 'default',
                    pointerEvents: spacebarHeld ? 'none' : (drawMode || hoveredHandle || dragHandle) ? 'auto' : 'none',
                  }}
                  onMouseDown={!spacebarHeld && (drawMode || hoveredHandle) ? handleCanvasMouseDown : undefined}
                  onMouseMove={!spacebarHeld && (drawMode || hoveredHandle || dragHandle) ? handleCanvasMouseMove : undefined}
                  onMouseUp={!spacebarHeld && (drawMode || dragHandle) ? handleCanvasMouseUp : undefined}
                />
              </div>
            )}
          </div>

          {/* Audit List */}
          <div className="w-[500px] bg-white border-l border-neutral-200 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center bg-neutral-50">
              <h4 className="font-black text-neutral-800 text-xs uppercase tracking-widest">Page Audit Log</h4>
            </div>
            
            <div className="flex-1 overflow-y-auto divide-y divide-neutral-100">
              {spots.map((spot) => {
                const state = qcState[`spot-${spot.spotId}`] || {};
                const hasReviewedProduct = (spot.products || []).some(p => {
                  const ps = qcState[`product-${p.upc}-spot-${spot.spotId}`];
                  return Boolean(ps?.issueType || ps?.status);
                });
                const isSpotReviewed = Boolean(state.status || state.issueType) || hasReviewedProduct;
                return (
                  <div
                    key={spot.spotId}
                    className={`p-4 transition-colors border-l-4 ${
                      state.status === 'fail'
                        ? 'bg-error-50/30 border-l-error-400'
                        : state.status === 'pass'
                          ? 'bg-success-50/20 border-l-success-400'
                          : isSpotReviewed
                            ? 'border-l-success-300'
                            : 'bg-warning-50/60 border-l-warning-400'
                    }`}
                  >
                    <div className="flex gap-4">
                        <div 
                          className="w-20 h-20 flex-shrink-0 border bg-white rounded overflow-hidden shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => setSelectedSpotImage(spot.spotImage)}
                        >
                        <img src={spot.spotImage} className="w-full h-full object-contain" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-black text-neutral-400">SPOT ID: {spot.spotId}</span>
                            {!isSpotReviewed && (
                              <span className="inline-flex items-center gap-1 w-fit text-[9px] font-bold uppercase tracking-wide text-warning-700 bg-warning-100 px-1.5 py-0.5 rounded-full">
                                <AlertCircle size={10} /> Not Reviewed
                              </span>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => toggleStatus('spot', spot.spotId, 'pass')}
                              className={`p-1 rounded transition-colors ${state.status === 'pass' ? 'bg-success-500 text-white' : 'bg-neutral-100 text-neutral-300 hover:text-success-500'}`}
                            >
                              <Check size={14} />
                            </button>
                            {/* X is now a display-only indicator. It lights up
                                automatically when either the spot has an
                                issueType selected OR any product on this spot
                                does. Users can't click it directly — that path
                                created "phantom" errors that lit the badge but
                                never reached the ledger. To remove the X, clear
                                the issueType(s) that triggered it. */}
                            {(() => {
                              const hasSpotIssue = Boolean(state.issueType);
                              const hasProductIssue = (spot.products || []).some(p => {
                                return Boolean(qcState[`product-${p.upc}-spot-${spot.spotId}`]?.issueType);
                              });
                              const derivedFail = hasSpotIssue || hasProductIssue;
                              const tooltip = derivedFail
                                ? (hasSpotIssue
                                    ? 'Marked as Fail because a Spot Issue is selected'
                                    : 'Marked as Fail because at least one product on this spot has an issue')
                                : 'Auto-marks when a Spot Issue or Product Issue is selected';
                              return (
                                <span
                                  title={tooltip}
                                  aria-label={tooltip}
                                  className={`p-1 rounded transition-colors cursor-not-allowed select-none ${derivedFail ? 'bg-error-500 text-white' : 'bg-neutral-100 text-neutral-300'}`}
                                >
                                  <X size={14} />
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="space-y-2 mb-3">
                          <div className="bg-primary-50 border-l-4 border-primary-500 pl-3 py-2 mb-2 min-h-[60px]">
                            <div className="font-bold text-primary-700 text-sm leading-tight">
                              {spot.products && spot.products.length > 0 
                                ? [...new Set(spot.products.map(p => p.brand).filter(Boolean))].join(' - ') || 'No Brand'
                                : 'No Products'}
                            </div>
                            <div className="text-xs text-primary-600 leading-tight">
                              {spot.products && spot.products.length > 0
                                ? [...new Set(spot.products.map(p => p.category).filter(Boolean))].join(' - ') || 'No Category'
                                : 'No Products'}
                            </div>
                          </div>

                          {/* Spot Issue Type Section */}
                          <div className="space-y-2 mb-2">
                            <div>
                              <label className="text-[9px] font-bold text-neutral-400 block mb-1">Spot Issue Type</label>
                              <select
                                className="w-full text-[11px] border border-neutral-200 rounded p-1.5 outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                                value={state.issueType || ''}
                                onChange={(e) => updateIssueType('spot', spot.spotId, e.target.value)}
                              >
                                <option value="">Select issue...</option>
                                <option value="Missing Product">Missing Product</option>
                                <option value="Delete Spot">Delete Spot</option>
                              </select>
                            </div>
                            
                            {state.issueType === 'Missing Product' && (
                              <div>
                                <label className="text-[9px] font-bold text-neutral-400 block mb-1">Suggested UPC or Spot Number</label>
                                <input
                                  type="text"
                                  placeholder="Enter suggested UPC..."
                                  className="w-full text-[11px] border border-neutral-200 rounded p-1.5 outline-none focus:ring-1 focus:ring-primary-500"
                                  value={state.suggestedUPC || ''}
                                  onChange={(e) => updateSuggestedUPC(spot.spotId, e.target.value)}
                                  maxLength={20}
                                />
                              </div>
                            )}
                          </div>

                          {/* Spot Comment */}
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare size={12} className="text-neutral-300" />
                            <input 
                              type="text"
                              placeholder="Add spot observation..."
                              className="flex-1 text-[11px] bg-transparent border-b border-transparent hover:border-neutral-200 focus:border-primary-400 outline-none pb-0.5"
                              value={state.observation || ''}
                              onChange={(e) => updateObservation('spot', spot.spotId, e.target.value)}
                            />
                          </div>

                          <div className="space-y-2">
                            {spot.products.map((p, idx) => {
                              const productKey = `product-${p.upc}-spot-${spot.spotId}`;
                              const productState = qcState[productKey] || {};
                              const formatPriceQty = (qty, price) => {
                                if (price === null || price === undefined || String(price).trim() === '') return null;
                                const numPrice = parseFloat(String(price).replace(/[^0-9.]/g, ''));
                                if (Number.isNaN(numPrice)) return null;
                                const qtyNum = parseInt(qty, 10);
                                const qtyDisplay = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
                                return `${qtyDisplay} x $${numPrice.toFixed(2)}`;
                              };
                              const regularPrice = formatPriceQty(p.qtyReg, p.priceReg);
                              const specialPrice = formatPriceQty(p.qtySpe, p.priceSpe);
                              return (
                                <div key={idx} className={`text-[10px] border border-neutral-200 rounded p-2 ${productState.issueType ? 'bg-yellow-50 border-yellow-300' : 'bg-white'}`}>
                                  <div className="font-bold text-neutral-900 text-sm leading-snug mb-1">{p.description}</div>
                                  <a 
                                    href={`https://us-east-1.online.tableau.com/#/site/reesa/views/BuscadorREESA2025_4/HistorialParticipaciondelProducto?:iid=2&Customername=${encodeURIComponent(currentShopper.retailer)}&SelectedProduct=${encodeURIComponent(p.upc)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-800 hover:underline break-words whitespace-normal"
                                  >
                                    UPC: {p.upc}
                                  </a>
                                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                    {regularPrice && (
                                      <span className="inline-flex items-center rounded bg-neutral-100 px-2 py-1 text-xs font-bold text-neutral-800">
                                        {regularPrice}
                                      </span>
                                    )}
                                    {specialPrice && (
                                      <span className="inline-flex items-center gap-1 rounded bg-warning-100 px-2 py-1 text-xs font-bold text-warning-800">
                                        Special: {specialPrice}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Product/UPC Level Issues */}
                                  <div className="mt-2 space-y-1.5">
                                    <div>
                                      <label className="text-[9px] font-bold text-neutral-400 block mb-1">Product Issue</label>
                                      <select
                                        className="w-full text-[10px] border border-neutral-300 rounded p-1 outline-none focus:ring-1 focus:ring-warning-500 bg-white"
                                        value={productState.issueType || ''}
                                        onChange={(e) => updateProductIssueType(spot.spotId, p.upc, e.target.value)}
                                      >
                                        <option value="">No issue</option>
                                        <option value="Incorrect Product">Incorrect Product</option>
                                        <option value="Wrong Price">Wrong Price</option>
                                      </select>
                                    </div>

                                    <div>
                                      <label className="text-[9px] font-bold text-neutral-400 block mb-1">Product Observation</label>
                                      <textarea
                                        placeholder="Enter observation..."
                                        className="w-full text-[10px] border border-neutral-300 rounded p-1 outline-none focus:ring-1 focus:ring-warning-500 resize-none"
                                        rows="2"
                                        value={productState.observation || ''}
                                        onChange={(e) => updateProductObservation(spot.spotId, p.upc, e.target.value)}
                                      />
                                    </div>
                                    
                                    {productState.issueType === 'Incorrect Product' && (
                                      <div>
                                        <label className="text-[9px] font-bold text-neutral-400 block mb-1">Suggested UPC</label>
                                        <input
                                          type="text"
                                          placeholder="Enter correct UPC..."
                                          className="w-full text-[10px] border border-neutral-300 rounded p-1 outline-none focus:ring-1 focus:ring-warning-500"
                                          value={productState.suggestedUPC || ''}
                                          onChange={(e) => updateProductSuggestedUPC(spot.spotId, p.upc, e.target.value)}
                                          maxLength={20}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      {/* Error Summary Modal */}
      {showErrorSummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-neutral-200 flex items-center justify-between bg-error-50">
              <div className="flex items-center gap-3">
                <AlertCircle size={24} className="text-error-500" />
                <div>
                  <h3 className="text-lg font-bold text-neutral-800">Error Summary</h3>
                  <p className="text-sm text-neutral-500">{totalErrors} total error{totalErrors !== 1 ? 's' : ''} found</p>
                </div>
              </div>
              <button
                onClick={() => setShowErrorSummary(false)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {pageNumbers.map(p => {
                  const pageKey = `page-${currentShopper.id}-${p}`;
                  const pageData = qcState[pageKey];
                  const pageSpots = Object.values(currentShopper.pages[p].spots);
                  
                  // Collect all errors for this page
                  const pageErrors = [];
                  
                  // Page-level error
                  if (pageData?.issueType) {
                    const pageImage = currentShopper.pages[p]?.shopperImage;
                    pageErrors.push({
                      type: 'page',
                      pageNum: p,
                      pageImage: pageImage,
                      issueType: pageData.issueType,
                      observation: pageData.observation
                    });
                  }
                  
                  // Spot and product errors
                  pageSpots.forEach(spot => {
                    const spotKey = `spot-${spot.spotId}`;
                    const spotData = qcState[spotKey];
                    
                    // Check for product-level errors first
                    let hasProductError = false;
                    if (spot.products) {
                      spot.products.forEach(product => {
                        const productKey = `product-${product.upc}-spot-${spot.spotId}`;
                        const productData = qcState[productKey];
                        if (productData?.issueType) {
                          hasProductError = true;
                          pageErrors.push({
                            type: 'product',
                            pageNum: p,
                            spotId: spot.spotId,
                            spotImage: spot.spotImage,
                            product: product,
                            issueType: productData.issueType,
                            suggestedUPC: productData.suggestedUPC
                          });
                        }
                      });
                    }
                    
                    const hasSpotIssueType = Boolean(spotData?.issueType);

                    // Spot-level error only when an issueType is set — matches
                    // ledger semantics; orphan status='fail' is no longer reachable.
                    if (hasSpotIssueType) {
                      pageErrors.push({
                        type: 'spot',
                        pageNum: p,
                        spotId: spot.spotId,
                        spotImage: spot.spotImage,
                        products: spot.products,
                        issueType: spotData.issueType,
                        observation: spotData.observation,
                        suggestedUPC: spotData.suggestedUPC
                      });
                    }
                  });
                  
                  if (pageErrors.length === 0) return null;
                  
                  return (
                    <div key={p} className="border border-neutral-200 rounded-lg overflow-hidden">
                      <div className="bg-neutral-100 px-4 py-2 border-b border-neutral-200">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-neutral-700">Page {p}</h4>
                          <span className="text-xs bg-error-100 text-error-700 px-2 py-1 rounded font-semibold">
                            {pageErrors.length} error{pageErrors.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      
                      <div className="divide-y divide-neutral-100">
                        {pageErrors.map((error, idx) => {
                          if (error.type === 'page') {
                            return (
                              <div key={idx} className="p-4 bg-error-50/30">
                                <div className="flex items-start gap-3">
                                  {error.pageImage ? (
                                    <div 
                                      className="w-16 h-16 flex-shrink-0 border bg-white rounded overflow-hidden shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                                      onClick={() => setSelectedSpotImage(error.pageImage)}
                                    >
                                      <img src={error.pageImage} className="w-full h-full object-contain" />
                                    </div>
                                  ) : (
                                    <div className="w-16 h-16 bg-error-100 rounded flex items-center justify-center flex-shrink-0">
                                      <AlertCircle size={24} className="text-error-500" />
                                    </div>
                                  )}
                                  <div className="flex-1">
                                    <div className="text-xs font-bold text-error-600 mb-1 uppercase">Page-Level Issue</div>
                                    <div className="text-sm font-semibold text-neutral-700 mb-2">{error.issueType}</div>
                                    {error.observation && (
                                      <div className="text-xs text-neutral-600 bg-white px-2 py-1 rounded border border-neutral-200">
                                        {error.observation}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (error.type === 'product') {
                            return (
                              <div key={idx} className="p-4 bg-yellow-50/30">
                                <div className="flex gap-3">
                                  <div 
                                    className="w-16 h-16 flex-shrink-0 border bg-white rounded overflow-hidden shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                                    onClick={() => setSelectedSpotImage(error.spotImage)}
                                  >
                                    <img src={error.spotImage} className="w-full h-full object-contain" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs font-bold text-warning-600 mb-1 uppercase">Product/UPC Issue - Spot {error.spotId}</div>
                                    <div className="text-sm font-semibold text-neutral-700 mb-1">{error.product.description}</div>
                                    <div className="text-xs text-primary-600 mb-2">UPC: {error.product.upc}</div>
                                    <div className="flex gap-2 items-center">
                                      <span className="text-xs bg-warning-100 text-warning-700 px-2 py-1 rounded font-semibold">
                                        {error.issueType}
                                      </span>
                                      {error.suggestedUPC && (
                                        <span className="text-xs text-neutral-600">
                                          Suggested: <span className="font-semibold">{error.suggestedUPC}</span>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <div key={idx} className="p-4">
                                <div className="flex gap-3">
                                  <div 
                                    className="w-16 h-16 flex-shrink-0 border bg-white rounded overflow-hidden shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                                    onClick={() => setSelectedSpotImage(error.spotImage)}
                                  >
                                    <img src={error.spotImage} className="w-full h-full object-contain" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs font-bold text-error-600 mb-1 uppercase">Spot Issue - Spot {error.spotId}</div>
                                    {error.issueType && (
                                      <div className="text-xs bg-error-100 text-error-700 px-2 py-1 rounded font-semibold inline-block mb-2">
                                        {error.issueType}
                                      </div>
                                    )}
                                    {error.products && error.products.length > 0 && (
                                      <div className="text-xs text-neutral-600 mb-1">
                                        {error.products.map(p => p.description).join(', ')}
                                      </div>
                                    )}
                                    {error.observation && (
                                      <div className="text-xs text-neutral-600 bg-neutral-50 px-2 py-1 rounded border border-neutral-200">
                                        {error.observation}
                                      </div>
                                    )}
                                    {error.suggestedUPC && (
                                      <div className="text-xs text-neutral-600 mt-1">
                                        Suggested: <span className="font-semibold">{error.suggestedUPC}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="p-4 border-t border-neutral-200 bg-neutral-50">
              <button
                onClick={() => setShowErrorSummary(false)}
                className="w-full bg-neutral-900 text-white py-2 rounded-lg font-semibold hover:bg-neutral-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Product Form Modal */}
      {showNewProductForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-neutral-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-neutral-800 flex items-center gap-2">
                  <Plus className="text-success-500" size={24} />
                  Create New Product
                </h3>
                <button
                  onClick={() => {
                    if (isSavingNewProduct) return;
                    setShowNewProductForm(false);
                    setNewProduct({});
                  }}
                  disabled={isSavingNewProduct}
                  className="text-neutral-400 hover:text-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {newProductError && (
                <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
                  <p className="text-sm font-medium text-error-700">{newProductError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Spot Link
                  </label>
                  <input
                    type="text"
                    value={newProduct.spotNumberLink || ''}
                    onChange={(e) => handleNewProductChange('spotNumberLink', e.target.value)}
                    onBlur={handleSpotLinkBlur}
                    placeholder="Paste the spot link"
                    aria-invalid={Boolean(spotLinkError)}
                    aria-describedby="spot-link-hint"
                    className={`w-full px-3 py-2 border rounded text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 ${
                      spotLinkError
                        ? 'border-error-400 focus:ring-error-500'
                        : 'border-neutral-200 focus:ring-success-500'
                    }`}
                  />
                  {/* One line under the field at a time: the format while the
                      value is fine, the complaint once it isn't. Mirrors the
                      `error || helperText` rule in the shared Input component. */}
                  <p
                    id="spot-link-hint"
                    className={`mt-1 text-xs ${spotLinkError ? 'text-error-600' : 'text-neutral-400'}`}
                  >
                    {spotLinkError || SPOT_LINK_FORMAT_HINT}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Suggested Description
                  </label>
                  <input
                    type="text"
                    value={newProduct.suggestedDescription || ''}
                    onChange={(e) => handleNewProductChange('suggestedDescription', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Reference Link
                  </label>
                  <input
                    type="text"
                    value={newProduct.referenceLink || ''}
                    onChange={(e) => handleNewProductChange('referenceLink', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Variety Type <span className="text-error-500">*</span>
                  </label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="varietyType"
                        value="New Variety"
                        checked={newProduct.varietyType === 'New Variety'}
                        onChange={(e) => handleNewProductChange('varietyType', e.target.value)}
                        className="w-4 h-4 text-success-600 focus:ring-success-500"
                      />
                      <span className="text-sm font-medium text-neutral-700">New Variety</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="varietyType"
                        value="Extend Existing Variety"
                        checked={newProduct.varietyType === 'Extend Existing Variety'}
                        onChange={(e) => handleNewProductChange('varietyType', e.target.value)}
                        className="w-4 h-4 text-success-600 focus:ring-success-500"
                      />
                      <span className="text-sm font-medium text-neutral-700">Extend Existing Variety</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-neutral-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  if (isSavingNewProduct) return;
                  setShowNewProductForm(false);
                  setNewProduct({});
                }}
                disabled={isSavingNewProduct}
                className="px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={saveNewProduct}
                disabled={isSavingNewProduct}
                className="px-4 py-2 bg-success-600 text-white rounded-lg text-sm font-semibold hover:bg-success-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save size={16} />
                {isSavingNewProduct ? 'Creating...' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
